import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import PortalLayout from './PortalLayout';
import { productService, addonService, inventoryService, categoryService, translationService, diningOptionService, modifierService } from '../../services/api';
import ModifierGroupsTab from './modifiers/ModifierGroupsTab';
import ProductOptionsTab from './modifiers/ProductOptionsTab';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import IconPicker from '../../components/common/IconPicker';
import { renderEntityImage } from '../../utils/imageUtils';
import BulkAddModal from '../../components/BulkAddModal';
import RecipeImportModal from '../../components/RecipeImportModal';
import useIsMobile from '../../hooks/useIsMobile';
import MobileCard from '../../components/common/MobileCard';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const AVATAR_PALETTE = ['#2563eb','#2563eb','#7c3aed','#d97706','#0891b2','#dc2626','#0d9488','#be185d'];
const avatarColor = (name = '') => AVATAR_PALETTE[(name.charCodeAt(0) || 0) % AVATAR_PALETTE.length];

const StockBadge = ({ product, autoStock, t }) => {
    if (product.infinite_stock) return <span style={{ fontSize: 11, fontWeight: 600, borderRadius: 5, padding: '2px 8px', background: '#eff6ff', color: '#2563eb' }}>{t('unlimited_symbol')}</span>;
    if (product.stock_calculation_type === 'auto') {
        if (autoStock === undefined) return <span style={{ fontSize: 11, fontWeight: 600, borderRadius: 5, padding: '2px 8px', background: '#f8fafc', color: '#64748b' }}>{t('calculating')}</span>;
        if (autoStock === Infinity || autoStock > 9999) return <span style={{ fontSize: 11, fontWeight: 600, borderRadius: 5, padding: '2px 8px', background: '#eff6ff', color: '#2563eb' }}>{t('unlimited_symbol')}</span>;
        if (autoStock <= 0) return <span style={{ fontSize: 11, fontWeight: 600, borderRadius: 5, padding: '2px 8px', background: '#fef2f2', color: '#dc2626' }}>{t('out_of_stock_count', { count: 0 })}</span>;
        if (autoStock <= 10) return <span style={{ fontSize: 11, fontWeight: 600, borderRadius: 5, padding: '2px 8px', background: '#fffbeb', color: '#d97706' }}>{autoStock} {t('low')}</span>;
        return <span style={{ fontSize: 11, fontWeight: 600, borderRadius: 5, padding: '2px 8px', background: '#eff5ff', color: '#16a34a' }}>{autoStock} {t('auto')}</span>;
    }
    const qty = product.quantity || 0;
    if (qty <= 0) return <span style={{ fontSize: 11, fontWeight: 600, borderRadius: 5, padding: '2px 8px', background: '#fef2f2', color: '#dc2626' }}>{t('out_of_stock')}</span>;
    if (qty <= 10) return <span style={{ fontSize: 11, fontWeight: 600, borderRadius: 5, padding: '2px 8px', background: '#fffbeb', color: '#d97706' }}>{qty} {t('low')}</span>;
    return <span style={{ fontSize: 11, fontWeight: 600, borderRadius: 5, padding: '2px 8px', background: '#eff5ff', color: '#16a34a' }}>{qty} {t('in_stock')}</span>;
};

const StatusBadge = ({ status, t }) => {
    const active = (status || '').toLowerCase() === 'active';
    return <span style={{ fontSize: 11, fontWeight: 600, borderRadius: 5, padding: '2px 8px', background: active ? '#eff5ff' : '#f1f5f9', color: active ? '#166534' : '#64748b' }}>{active ? t('active') : t('inactive')}</span>;
};

// ─── Cost with source badge ──────────────────────────────────────────────
// Shows the resolver-derived cost (from recipe when present, fallback to
// manual cost_price) plus a small coloured badge. Sources:
//   recipe         -> green  "Recipe"   (computed, auto-updating)
//   recipe_partial -> amber  "Partial"  (some ingredients missing cost)
//   manual         -> grey   "Manual"   (product.cost_price within sanity)
//   unknown        -> red    "Unknown"  (placeholder / no data — flagged)
const CostWithSource = ({ product, currency, t }) => {
    const src = product.cost_source || (product.cost_price ? 'manual' : 'unknown');
    const cost = product.effective_cost_price != null
        ? product.effective_cost_price
        : Number(product.cost_price || 0);
    const palette = {
        recipe:         { bg: '#eff5ff', fg: '#166534', label: t('cost_source_recipe') },
        recipe_partial: { bg: '#fffbeb', fg: '#d97706', label: t('cost_source_recipe_partial_short') },
        manual:         { bg: '#f1f5f9', fg: '#475569', label: t('cost_source_manual') },
        unknown:        { bg: '#fef2f2', fg: '#dc2626', label: t('cost_source_unknown_short') },
    };
    const p = palette[src] || palette.unknown;
    const tooltip = src === 'unknown'
        ? (product.cost_warnings || []).join(' · ') || t('cost_source_unknown')
        : src === 'recipe_partial'
            ? t('cost_source_recipe_partial')
            : src === 'recipe'
                ? t('cost_source_recipe_tooltip')
                : t('cost_source_manual_tooltip');
    return (
        <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-end', gap: 3 }} title={tooltip}>
            <span style={{ fontWeight: 600, color: src === 'unknown' ? '#dc2626' : '#475569' }}>
                {cost > 0 ? `${currency}${Number(cost).toFixed(2)}` : '—'}
            </span>
            <span style={{
                fontSize: 9, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase',
                padding: '2px 6px', borderRadius: 4, background: p.bg, color: p.fg,
            }}>
                {p.label}
            </span>
        </div>
    );
};

const ProductAvatar = ({ name, imageUrl, size = 36 }) => {
    const img = renderEntityImage(imageUrl, size);
    if (img) return img;
    return (
        <div style={{ width: size, height: size, borderRadius: 8, background: avatarColor(name), display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: size * 0.38, fontWeight: 700, flexShrink: 0 }}>
            {(name || '?').charAt(0).toUpperCase()}
        </div>
    );
};

// ─── Product Modal ─────────────────────────────────────────────────────────────
const generateProductCode = (category, allProducts) => {
    if (!category) return '';
    const prefix = category.slice(0, 3).toUpperCase();
    const existing = (allProducts || []).filter(p =>
        p.product_code && p.product_code.toUpperCase().startsWith(prefix)
    );
    let maxNum = 0;
    existing.forEach(p => {
        const suffix = p.product_code.slice(prefix.length);
        const num = parseInt(suffix, 10);
        if (!isNaN(num) && num > maxNum) maxNum = num;
    });
    const next = maxNum + 1;
    return `${prefix}${String(next).padStart(3, '0')}`;
};

// ─── Add-ons assignment editor (inside the product modal) ────────────────
const ProductAddonsEditor = ({ productCode, productCategory, t, toast }) => {
    const [allAddons, setAllAddons] = useState([]);
    const [selectedCodes, setSelectedCodes] = useState(new Set());
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!productCode) return;
        let active = true;
        setLoading(true);
        Promise.all([
            addonService.getAllAddons(),
            productService.getProduct(productCode),
        ]).then(([addons, product]) => {
            if (!active) return;
            setAllAddons(addons || []);
            const assigned = (product?.available_addons || []).map(a => a.product_code);
            setSelectedCodes(new Set(assigned));
        }).catch(() => {
            if (active) toast?.(t('failed_to_load'), 'error');
        }).finally(() => {
            if (active) setLoading(false);
        });
        return () => { active = false; };
    }, [productCode]);

    const toggle = (code) => {
        setSelectedCodes(prev => {
            const next = new Set(prev);
            if (next.has(code)) next.delete(code);
            else next.add(code);
            return next;
        });
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await productService.assignAddons(productCode, Array.from(selectedCodes));
            toast?.(t('addons_saved'), 'success');
        } catch {
            toast?.(t('failed_to_save'), 'error');
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div style={{ textAlign: 'center', padding: 30, color: '#64748b', fontSize: 13 }}>{t('loading')}</div>;

    const parseCategory = (cat) => {
        if (!cat) return [];
        if (Array.isArray(cat)) return cat;
        if (typeof cat === 'string' && cat.startsWith('[')) try { return JSON.parse(cat); } catch { return [cat]; }
        return [cat];
    };

    const filteredAddons = allAddons.filter(addon => {
        const addonCats = parseCategory(addon.category);
        return addonCats.includes(productCategory);
    });

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <p style={{ margin: 0, fontSize: 12, color: '#64748b' }}>
                {t('select_addons_for_product')}
            </p>
            {filteredAddons.length === 0 ? (
                <p style={{ color: '#94a3b8', fontSize: 13, textAlign: 'center', padding: 20 }}>{t('no_addons_available')}</p>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 350, overflowY: 'auto' }}>
                    {filteredAddons.map(addon => {
                        const checked = selectedCodes.has(addon.product_code);
                        const cats = parseCategory(addon.category);
                        return (
                            <label key={addon.product_code} style={{
                                display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
                                border: `1.5px solid ${checked ? '#2563eb' : '#e2e8f0'}`,
                                borderRadius: 8, cursor: 'pointer',
                                background: checked ? '#eff5ff' : '#fff',
                                transition: 'all 0.12s',
                            }}>
                                <input type="checkbox" checked={checked} onChange={() => toggle(addon.product_code)}
                                    style={{ accentColor: '#2563eb', width: 16, height: 16 }} />
                                <div style={{ flex: 1 }}>
                                    <div style={{ fontSize: 13, fontWeight: 600, color: '#212529' }}>{addon.name}</div>
                                    <div style={{ fontSize: 11, color: '#64748b' }}>{cats.join(', ')} · {addon.product_code}</div>
                                </div>
                                <span style={{ fontSize: 13, fontWeight: 700, color: '#2563eb' }}>£{parseFloat(addon.price || 0).toFixed(2)}</span>
                            </label>
                        );
                    })}
                </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
                <span style={{ fontSize: 12, color: '#64748b' }}>{selectedCodes.size} {t('selected')}</span>
                <button onClick={handleSave} disabled={saving}
                    style={{ padding: '8px 18px', borderRadius: 7, border: 'none', background: '#2563eb', color: '#fff', fontSize: 13, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.6 : 1 }}>
                    {saving ? t('saving') + '…' : t('save_addons')}
                </button>
            </div>
        </div>
    );
};

// ─── Recipe editor (inside the product modal) ─────────────────────────────
// Handles CRUD of product_ingredients rows for a single product. Lives inside
// ProductModal so everything about a product — details + recipe — is in one
// place. Disabled on "Create" because the endpoint keys on product_code.
const slugify = (name) => name.toLowerCase().replace(/\s+/g, '_');
const UNIVERSAL_DINING = new Set(['all', 'both']);

// Multi-select dropdown for dining_option. "All Options" is a master toggle
// that ticks/unticks every individual option (Excel-filter semantics); the
// master checkbox auto-reflects whether every individual option is ticked.
const DiningOptionsMultiSelect = ({ options, allSlugs, selected, onChange, t }) => {
    const [open, setOpen] = useState(false);
    const ref = React.useRef(null);

    // Close on outside click — keeps the popover lightweight without a portal.
    useEffect(() => {
        if (!open) return;
        const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, [open]);

    const allSelected = allSlugs.length > 0 && allSlugs.every(s => selected.has(s));
    const noneSelected = selected.size === 0;

    const toggleAll = () => {
        if (allSelected) onChange(new Set());
        else onChange(new Set(allSlugs));
    };
    const toggleOne = (slug) => {
        const next = new Set(selected);
        if (next.has(slug)) next.delete(slug);
        else next.add(slug);
        onChange(next);
    };

    const triggerLabel = allSelected
        ? t('all_options')
        : noneSelected
            ? t('no_options_selected')
            : options
                .filter(d => selected.has(slugify(d.name)))
                .map(d => d.name)
                .join(', ');

    const triggerStyle = {
        padding: '7px 9px', border: '1.5px solid #e2e8f0', borderRadius: 6,
        fontSize: 13, width: '100%', boxSizing: 'border-box', color: '#212529',
        background: '#fff', textAlign: 'left', cursor: 'pointer',
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
    };

    return (
        <div ref={ref} style={{ position: 'relative' }}>
            <button type="button" onClick={() => setOpen(o => !o)} style={triggerStyle}>
                {triggerLabel}
                <span style={{ float: 'right', color: '#94a3b8', fontSize: 10 }}>▼</span>
            </button>
            {open && (
                <div style={{
                    position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4,
                    background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 8,
                    boxShadow: '0 4px 16px rgba(0,0,0,0.08)', zIndex: 50,
                    maxHeight: 240, overflowY: 'auto',
                }}>
                    {/* Master "All Options" toggle */}
                    <label
                        onClick={(e) => { e.preventDefault(); toggleAll(); }}
                        style={{
                            display: 'flex', alignItems: 'center', gap: 8,
                            padding: '8px 12px', cursor: 'pointer',
                            borderBottom: '1px solid #f1f5f9',
                            fontSize: 12, fontWeight: 700, color: '#1d4ed8',
                            background: allSelected ? '#eff5ff' : '#f8fafc',
                        }}
                    >
                        <input
                            type="checkbox"
                            checked={allSelected}
                            readOnly
                            style={{ accentColor: '#2563eb', cursor: 'pointer' }}
                        />
                        {t('all_options')}
                    </label>
                    {options.map(d => {
                        const slug = slugify(d.name);
                        const checked = selected.has(slug);
                        return (
                            <label
                                key={d.id}
                                onClick={(e) => { e.preventDefault(); toggleOne(slug); }}
                                style={{
                                    display: 'flex', alignItems: 'center', gap: 8,
                                    padding: '7px 12px', cursor: 'pointer',
                                    fontSize: 12, color: '#212529',
                                    background: checked ? '#eff5ff' : '#fff',
                                }}
                                onMouseEnter={(e) => { if (!checked) e.currentTarget.style.background = '#f8fafc'; }}
                                onMouseLeave={(e) => { if (!checked) e.currentTarget.style.background = '#fff'; }}
                            >
                                <input
                                    type="checkbox"
                                    checked={checked}
                                    readOnly
                                    style={{ accentColor: '#2563eb', cursor: 'pointer' }}
                                />
                                {d.name}
                            </label>
                        );
                    })}
                    {options.length === 0 && (
                        <div style={{ padding: '10px 12px', fontSize: 12, color: '#94a3b8' }}>
                            {t('no_dining_options_configured')}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

// ─── Unit conversion helpers (recipe ↔ stock) ───────────────────────────────
const UNIT_CONVERSIONS = {
    'litres': { small: 'ml', factor: 1000 },
    'liters': { small: 'ml', factor: 1000 },
    'kg': { small: 'grams', factor: 1000 },
};

const convertToStockUnit = (value, recipeUnit, stockUnit) => {
    if (recipeUnit === stockUnit) return value;
    const conv = UNIT_CONVERSIONS[stockUnit];
    if (conv && recipeUnit === conv.small) return value / conv.factor;
    return value;
};

const convertFromStockUnit = (value, recipeUnit, stockUnit) => {
    if (recipeUnit === stockUnit) return value;
    const conv = UNIT_CONVERSIONS[stockUnit];
    if (conv && recipeUnit === conv.small) return value * conv.factor;
    return value;
};

const getDisplayUnit = (inv) => inv.recipe_unit || inv.unit_of_measurement;

const ProductRecipeEditor = ({ productCode, sellingPrice, currency, t, toast, onRecipeSaved }) => {
    const [inventory, setInventory] = useState([]);
    const [diningOptions, setDiningOptions] = useState([]);
    // Each UI row now carries a *set* of dining_option slugs. "All Options" =
    // a set containing every known slug (stored that way for easy toggling);
    // on save, a full set collapses to a single `dining_option='all'` DB row.
    const [rows, setRows] = useState([]);
    const [existingCount, setExistingCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [showCopyPicker, setShowCopyPicker] = useState(false);
    const [copySearch, setCopySearch] = useState('');
    const [copyProducts, setCopyProducts] = useState([]);

    const allSlugs = useMemo(() => diningOptions.map(d => slugify(d.name)), [diningOptions]);

    useEffect(() => {
        if (!productCode) return;
        let active = true;
        setLoading(true);
        // Dining options live in the `dining_options` table — fetch via the
        // dedicated endpoint. The tenant config JSON's dining_options key has
        // been deprecated and returns null on every modern tenant.
        Promise.all([
            inventoryService.getAllInventory(),
            inventoryService.getProductIngredients(productCode),
            diningOptionService.getAll(),
        ]).then(([invList, existing, dOpts]) => {
            if (!active) return;
            setInventory(invList || []);
            const activeOpts = (dOpts || []).filter(d => d.is_active !== false);
            setDiningOptions(activeOpts);
            // DB rows come back one per (ingredient, dining_option). Group them
            // by (inventory_id, quantity) so the UI shows a single row with the
            // set of selected dining options.
            const optSlugs = activeOpts.map(d => slugify(d.name));
            const grouped = {};
            (existing || []).forEach(i => {
                // Convert stock-unit quantity to recipe-unit for display
                const inv = (invList || []).find(x => String(x.inventory_id) === String(i.inventory_id));
                const recipeUnit = inv ? getDisplayUnit(inv) : null;
                const stockUnit = inv ? inv.unit_of_measurement : null;
                const displayQty = (inv && recipeUnit && stockUnit)
                    ? convertFromStockUnit(i.quantity_needed, recipeUnit, stockUnit)
                    : i.quantity_needed;
                const key = `${i.inventory_id}|${i.quantity_needed}`;
                if (!grouped[key]) {
                    grouped[key] = {
                        inventory_id: String(i.inventory_id),
                        quantity_needed: String(displayQty),
                        dining_option_set: new Set(),
                    };
                }
                const dopt = (i.dining_option || 'all').toLowerCase();
                if (UNIVERSAL_DINING.has(dopt)) {
                    // 'all'/'both' = every slug
                    optSlugs.forEach(s => grouped[key].dining_option_set.add(s));
                } else {
                    grouped[key].dining_option_set.add(dopt);
                }
            });
            const mapped = Object.values(grouped);
            // Count what the user can actually see. An ingredient spanning
            // three dining options is three DB rows but one visible card, and
            // "Recipe has 5 ingredients" above three cards reads as a bug.
            setExistingCount(mapped.length);
            setRows(mapped.length > 0
                ? mapped
                : [{ inventory_id: '', quantity_needed: '', dining_option_set: new Set(optSlugs) }]);
        }).catch(() => {
            if (!active) return;
            toast?.(t('failed_to_load_recipe'), 'error');
            setRows([{ inventory_id: '', quantity_needed: '', dining_option_set: new Set() }]);
        }).finally(() => {
            if (active) setLoading(false);
        });
        return () => { active = false; };
    }, [productCode, toast, t]);

    const addRow = () => setRows(r => [...r, {
        inventory_id: '', quantity_needed: '', dining_option_set: new Set(allSlugs),
    }]);
    const removeRow = (i) => setRows(r => r.filter((_, idx) => idx !== i));
    const updateRow = (i, field, value) => setRows(r => {
        const next = [...r];
        next[i] = { ...next[i], [field]: value };
        return next;
    });
    const setRowDiningSet = (i, newSet) => setRows(r => {
        const next = [...r];
        next[i] = { ...next[i], dining_option_set: new Set(newSet) };
        return next;
    });

    const loadCopyProducts = async () => {
        try {
            const prods = await productService.getAllProducts();
            const others = prods.filter(p => p.product_code !== productCode);
            const withRecipe = [];
            for (const p of others) {
                try {
                    const recipe = await inventoryService.getProductIngredients(p.product_code);
                    if (recipe?.length > 0) {
                        withRecipe.push({ ...p, ingredientCount: recipe.length });
                    }
                } catch (e) { /* no recipe */ }
            }
            setCopyProducts(withRecipe);
        } catch (e) { /* ignore */ }
    };

    const handleCopyRecipe = async (source) => {
        if (!window.confirm(t('copy_recipe_confirm').replace('{name}', source.name).replace('{count}', source.ingredientCount))) return;
        try {
            const recipe = await inventoryService.getProductIngredients(source.product_code);
            const optSlugs = diningOptions.map(d => slugify(d.name));
            setRows(recipe.map(r => {
                // Convert stock-unit quantity to recipe-unit for display
                const inv = inventory.find(x => String(x.inventory_id) === String(r.inventory_id));
                const recipeUnit = inv ? getDisplayUnit(inv) : null;
                const stockUnit = inv ? inv.unit_of_measurement : null;
                const displayQty = (inv && recipeUnit && stockUnit)
                    ? convertFromStockUnit(r.quantity_needed, recipeUnit, stockUnit)
                    : r.quantity_needed;
                return {
                inventory_id: String(r.inventory_id),
                quantity_needed: String(displayQty),
                dining_option_set: new Set(
                    UNIVERSAL_DINING.has((r.dining_option || 'all').toLowerCase())
                        ? optSlugs
                        : [(r.dining_option || 'all').toLowerCase()]
                ),
            }; }));
            setShowCopyPicker(false);
            setCopySearch('');
            toast?.(t('recipe_copied'), 'success');
        } catch (e) {
            toast?.(t('failed_to_copy_recipe'), 'error');
        }
    };

    const ingredientCost = rows.reduce((sum, row) => {
        const inv = inventory.find(i => String(i.inventory_id) === row.inventory_id);
        if (!inv) return sum;
        // quantity_needed is in recipe_unit; cost_per_unit is per stock_unit
        // Convert recipe quantity to stock units for cost calculation
        const recipeUnit = getDisplayUnit(inv);
        const qtyInStock = convertToStockUnit(parseFloat(row.quantity_needed) || 0, recipeUnit, inv.unit_of_measurement);
        return sum + (parseFloat(inv.cost_per_unit) || 0) * qtyInStock;
    }, 0);
    const selling = parseFloat(sellingPrice) || 0;
    const grossMargin = selling - ingredientCost;
    const marginPct = selling > 0 ? (grossMargin / selling) * 100 : null;

    const handleSave = async () => {
        const valid = rows.filter(r =>
            r.inventory_id &&
            r.quantity_needed &&
            parseFloat(r.quantity_needed) > 0 &&
            r.dining_option_set.size > 0
        );
        setSaving(true);
        try {
            // Expand UI rows into DB rows:
            //   - full set (all options) → 1 row with dining_option='all' (cleaner data)
            //   - subset                 → N rows, one per slug
            const payload = [];
            valid.forEach(r => {
                const allSelected = allSlugs.length > 0 && r.dining_option_set.size === allSlugs.length;
                const slugsToSave = allSelected ? ['all'] : Array.from(r.dining_option_set);
                // Convert recipe-unit quantity back to stock-unit for storage
                const inv = inventory.find(i => String(i.inventory_id) === r.inventory_id);
                const recipeUnit = inv ? getDisplayUnit(inv) : null;
                const stockUnit = inv ? inv.unit_of_measurement : null;
                const qtyInStock = (inv && recipeUnit && stockUnit)
                    ? convertToStockUnit(parseFloat(r.quantity_needed), recipeUnit, stockUnit)
                    : parseFloat(r.quantity_needed);
                slugsToSave.forEach(slug => {
                    payload.push({
                        inventory_id: String(r.inventory_id),
                        quantity_needed: qtyInStock,
                        dining_option: slug,
                    });
                });
            });
            await inventoryService.setProductIngredients(productCode, payload);
            toast?.(t('recipe_saved_successfully'), 'success');
            // `valid`, not `payload` — payload is expanded one row per dining
            // option, so counting it would overstate what the user sees.
            setExistingCount(valid.length);
            onRecipeSaved?.();
        } catch {
            toast?.(t('failed_to_save_recipe'), 'error');
        } finally {
            setSaving(false);
        }
    };

    const labelStyle = { fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4, display: 'block' };
    const inputStyle = { padding: '7px 9px', border: '1.5px solid #e2e8f0', borderRadius: 6, fontSize: 13, width: '100%', boxSizing: 'border-box', color: '#212529' };

    if (loading) {
        return <div style={{ textAlign: 'center', padding: 30, color: '#64748b', fontSize: 13 }}>{t('loading_recipe')}</div>;
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {/* Cost summary strip */}
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 120, background: '#f8fafc', borderRadius: 8, padding: '8px 12px', border: '1px solid #e2e8f0' }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.7, marginBottom: 2 }}>{t('ingredient_cost')}</div>
                    <div style={{ fontSize: 14, fontWeight: 800, color: '#212529' }}>{currency}{ingredientCost.toFixed(3)}</div>
                </div>
                <div style={{ flex: 1, minWidth: 120, background: '#f8fafc', borderRadius: 8, padding: '8px 12px', border: '1px solid #e2e8f0' }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.7, marginBottom: 2 }}>{t('selling_price')}</div>
                    <div style={{ fontSize: 14, fontWeight: 800, color: '#212529' }}>{currency}{selling.toFixed(2)}</div>
                </div>
                <div style={{
                    flex: 1, minWidth: 140,
                    background: grossMargin >= 0 ? '#eff5ff' : '#fef2f2',
                    borderRadius: 8, padding: '8px 12px',
                    border: '1px solid ' + (grossMargin >= 0 ? '#86efac' : '#fca5a5'),
                }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.7, marginBottom: 2 }}>{t('gross_margin')}</div>
                    <div style={{ fontSize: 14, fontWeight: 800, color: grossMargin >= 0 ? '#2563eb' : '#dc2626' }}>
                        {currency}{grossMargin.toFixed(2)}
                        {marginPct !== null && <span style={{ fontSize: 11, marginLeft: 6 }}>({marginPct.toFixed(1)}%)</span>}
                    </div>
                </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <p style={{ margin: 0, fontSize: 12, color: '#64748b' }}>
                    {t('define_raw_ingredients_for_product')}
                </p>
                <div style={{ position: 'relative' }}>
                    <button
                        onClick={() => { loadCopyProducts(); setShowCopyPicker(!showCopyPicker); }}
                        style={{
                            padding: '6px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600,
                            border: '1px solid #2563eb', backgroundColor: '#fff', color: '#2563eb',
                            cursor: 'pointer', whiteSpace: 'nowrap',
                        }}
                    >{t('copy_from_product')}</button>
                    {showCopyPicker && (
                        <div style={{
                            position: 'absolute', top: '100%', right: 0, marginTop: 4,
                            backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: 10,
                            boxShadow: '0 4px 16px rgba(0,0,0,0.1)', width: 320, maxHeight: 300,
                            overflow: 'hidden', zIndex: 100,
                        }}>
                            <div style={{ padding: 8 }}>
                                <input
                                    type="text" value={copySearch} onChange={e => setCopySearch(e.target.value)}
                                    placeholder={t('search_products')}
                                    style={{ padding: '7px 9px', border: '1.5px solid #e2e8f0', borderRadius: 6, fontSize: 13, width: '100%', boxSizing: 'border-box' }}
                                    autoFocus
                                />
                            </div>
                            <div style={{ maxHeight: 240, overflowY: 'auto' }}>
                                {copyProducts
                                    .filter(p => p.name.toLowerCase().includes(copySearch.toLowerCase()))
                                    .map(p => (
                                        <div key={p.product_code} onClick={() => handleCopyRecipe(p)}
                                            style={{
                                                padding: '8px 12px', cursor: 'pointer', display: 'flex',
                                                justifyContent: 'space-between', alignItems: 'center',
                                                borderBottom: '1px solid #e2e8f0',
                                            }}
                                            onMouseEnter={e => e.currentTarget.style.backgroundColor = '#eff5ff'}
                                            onMouseLeave={e => e.currentTarget.style.backgroundColor = '#fff'}
                                        >
                                            <span style={{ fontSize: 13, color: '#212529' }}>{p.name}</span>
                                            <span style={{ fontSize: 11, color: '#64748b', backgroundColor: '#f8fafc', padding: '2px 8px', borderRadius: 10 }}>
                                                {p.ingredientCount} {t('ingredients')}
                                            </span>
                                        </div>
                                    ))}
                                {copyProducts.filter(p => p.name.toLowerCase().includes(copySearch.toLowerCase())).length === 0 && (
                                    <div style={{ padding: 16, textAlign: 'center', fontSize: 12, color: '#64748b' }}>{t('no_products_with_recipe')}</div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {rows.map((row, i) => {
                const chosen = inventory.find(inv => String(inv.inventory_id) === row.inventory_id);
                return (
                    <div key={i} style={{ border: '1.5px solid #e2e8f0', borderRadius: 9, padding: '12px 12px', background: '#f8fafc', display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1.2fr auto', gap: 10, alignItems: 'end' }}>
                            <label>
                                <span style={labelStyle}>{t('ingredient')} *</span>
                                <select value={row.inventory_id} onChange={e => updateRow(i, 'inventory_id', e.target.value)} style={inputStyle}>
                                    <option value="">{t('select_ingredient')}</option>
                                    {inventory.map(inv => (
                                        <option key={inv.inventory_id} value={String(inv.inventory_id)}>
                                            {inv.name} ({getDisplayUnit(inv)})
                                        </option>
                                    ))}
                                </select>
                            </label>
                            <label>
                                <span style={labelStyle}>{t('quantity')} ({chosen ? getDisplayUnit(chosen) : '*'})</span>
                                <input type="number" min="0" step="0.01" value={row.quantity_needed} onChange={e => updateRow(i, 'quantity_needed', e.target.value)} style={inputStyle} placeholder="0.00" />
                            </label>
                            <div>
                                <span style={labelStyle}>{t('dining_option')}</span>
                                <DiningOptionsMultiSelect
                                    options={diningOptions}
                                    allSlugs={allSlugs}
                                    selected={row.dining_option_set}
                                    onChange={(s) => setRowDiningSet(i, s)}
                                    t={t}
                                />
                            </div>
                            <button
                                type="button"
                                onClick={() => removeRow(i)}
                                title={t('remove_ingredient')}
                                style={{
                                    background: '#fef2f2',
                                    color: '#dc2626',
                                    border: 'none', borderRadius: 6, padding: '8px 10px',
                                    cursor: rows.length === 1 ? 'not-allowed' : 'pointer',
                                    display: 'flex', alignItems: 'center',
                                }}
                            >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="3 6 5 6 21 6"/>
                                    <path d="M19 6l-2 14a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L5 6"/>
                                    <path d="M10 11v6M14 11v6"/>
                                </svg>
                            </button>
                        </div>
                        {chosen && (() => {
                            const recipeUnit = getDisplayUnit(chosen);
                            const stockUnit = chosen.unit_of_measurement;
                            const qtyVal = parseFloat(row.quantity_needed) || 0;
                            const showConversion = recipeUnit !== stockUnit && qtyVal > 0;
                            const stockQty = showConversion ? convertToStockUnit(qtyVal, recipeUnit, stockUnit) : qtyVal;
                            return (
                                <div style={{ fontSize: 11, color: '#64748b' }}>
                                    {t('unit_cost')}: <strong style={{ color: '#212529' }}>{currency}{parseFloat(chosen.cost_per_unit || 0).toFixed(3)}/{stockUnit}</strong>
                                    {' · '}
                                    {t('stock_left')}: <strong style={{ color: '#212529' }}>{chosen.current_stock} {stockUnit}</strong>
                                    {showConversion && (
                                        <span style={{ marginLeft: 6, color: '#2563eb', fontWeight: 600 }}>
                                            ({qtyVal} {recipeUnit} = {stockQty.toFixed(4)} {stockUnit} {t('stock_conversion_note')})
                                        </span>
                                    )}
                                </div>
                            );
                        })()}
                    </div>
                );
            })}

            <button
                type="button"
                onClick={addRow}
                style={{ border: '2px dashed #e2e8f0', background: 'none', borderRadius: 9, padding: '10px 0', color: '#64748b', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
            >
                + {t('add_another_ingredient')}
            </button>

            <div style={{ display: 'flex', gap: 10, justifyContent: 'space-between', alignItems: 'center', paddingTop: 10, borderTop: '1px solid #e2e8f0' }}>
                <span style={{ fontSize: 12, color: '#64748b' }}>
                    {existingCount > 0
                        ? t('recipe_ingredients_count').replace('{n}', existingCount)
                        : t('no_recipe_defined')}
                </span>
                <button
                    type="button"
                    onClick={handleSave}
                    disabled={saving || (rows.length > 0 && rows.every(r => !r.inventory_id || !r.quantity_needed))}
                    style={{
                        padding: '8px 18px', borderRadius: 7, border: 'none',
                        background: '#2563eb', color: '#fff', fontSize: 13, fontWeight: 700,
                        cursor: saving ? 'not-allowed' : 'pointer',
                        opacity: saving ? 0.6 : 1,
                    }}
                >
                    {saving ? t('saving') + '…' : t('save_recipe')}
                </button>
            </div>
        </div>
    );
};


const ProductModal = ({ open, onClose, onSave, product, allProducts, currency, t, translationDetails, toast, defaultTab, allGroups = [], addons = [] }) => {
    const { hasFeature } = useAuth();
    const [form, setForm] = useState({});
    const [saving, setSaving] = useState(false);
    const [categories, setCategories] = useState([]);
    const [subcategories, setSubcategories] = useState([]);
    const [translating, setTranslating] = useState(false);
    const [translationOptions, setTranslationOptions] = useState(null);
    const [showTranslationPicker, setShowTranslationPicker] = useState(false);
    const [creatingCategory, setCreatingCategory] = useState(false);
    const [newCategoryName, setNewCategoryName] = useState('');
    const [creatingSub, setCreatingSub] = useState(false);
    const [newSubName, setNewSubName] = useState('');
    // Tabs: 'details' | 'recipe' | 'addons' | 'options'. Recipe/legacy-Add-ons
    // are only available when editing — setProductIngredients/ProductAddonsEditor
    // key on product_code which doesn't exist on create.
    const [activeTab, setActiveTab] = useState('details');
    const recipeAvailable = !!product?.product_code;
    // Attached option-group ids for the Options tab. Loaded fresh every time
    // the modal opens for an existing product — ProductOptionsTab's auto-save
    // replaces the product's *entire* group list on every attach/detach, so
    // rendering it with a stale or empty list would silently drop whatever
    // was already attached the moment the tenant touched it.
    const [attachedGroupIds, setAttachedGroupIds] = useState([]);
    const [optionsLoading, setOptionsLoading] = useState(false);

    useEffect(() => {
        if (open) {
            // Deep-link support: caller may request we open straight to the recipe tab.
            setActiveTab(defaultTab === 'recipe' && product?.product_code ? 'recipe' : 'details');
            setForm(product ? {
                name: product.name || '',
                product_code: product.product_code || '',
                category: product.category || '',
                subcategory: product.subcategory || '',
                cost_price: (product.stock_calculation_type === 'auto' && product.effective_cost_price != null) ? product.effective_cost_price : (product.cost_price ?? ''),
                selling_price: product.selling_price ?? '',
                secondary_price: product.secondary_price ?? '',
                quantity: product.quantity ?? 0,
                description: product.description || '',
                status: product.status || 'active',
                stock_calculation_type: product.stock_calculation_type || 'manual',
                infinite_stock: product.infinite_stock || false,
                image_url: product.image_url || null,
                translation_mode: product.translation_mode || null,
                manual_translation: product.manual_translation || null,
            } : {
                name: '', product_code: '', category: '', subcategory: '',
                cost_price: '', selling_price: '', secondary_price: '', quantity: 0,
                description: '', status: 'active', stock_calculation_type: 'manual', infinite_stock: false,
                image_url: null, translation_mode: null, manual_translation: null,
            });
            // Pre-populate translation options if mode already set
            if (product?.translation_mode && translationDetails?.[product.name]) {
                const ar = translationDetails[product.name]?.ar || {};
                setTranslationOptions({
                    translation: ar.translation || null,
                    transliteration: ar.transliteration || null,
                    community_manuals: ar.community_manuals || [],
                    suggestion: null,
                });
            } else {
                setTranslationOptions(null);
            }
            setShowTranslationPicker(false);
            setCreatingCategory(false);
            setNewCategoryName('');
            setCreatingSub(false);
            setNewSubName('');
        }
    }, [open, product, translationDetails]);

    useEffect(() => {
        const onKey = e => { if (e.key === 'Escape') onClose(); };
        if (open) window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, onClose]);

    useEffect(() => {
        if (!open || !product?.product_code) {
            setAttachedGroupIds([]);
            return;
        }
        let active = true;
        setOptionsLoading(true);
        modifierService.getProductGroups(product.product_code)
            .then(data => { if (active) setAttachedGroupIds(data?.group_ids || []); })
            .catch(() => { if (active) setAttachedGroupIds([]); })
            .finally(() => { if (active) setOptionsLoading(false); });
        return () => { active = false; };
    }, [open, product?.product_code]);

    useEffect(() => {
        categoryService.getAll().then(setCategories).catch(console.error);
    }, []);

    useEffect(() => {
        if (form.category) {
            const selectedCat = categories.find(c => c.name === form.category);
            if (selectedCat) {
                categoryService.getSubcategories(selectedCat.id)
                    .then(setSubcategories)
                    .catch(console.error);
            } else {
                setSubcategories([]);
            }
            // Auto-generate product code for new products when category changes
            if (!product) {
                const code = generateProductCode(form.category, allProducts);
                setForm(f => ({ ...f, product_code: code }));
            }
        } else {
            setSubcategories([]);
        }
    }, [form.category, categories, product, allProducts]);

    if (!open) return null;

    const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

    const handleTranslate = async () => {
        if (!form.name) return;
        setTranslating(true);
        try {
            const result = await translationService.translateProductName(form.name, form.category || 'product');
            const aiFailed = result.translation === form.name && result.transliteration === form.name;
            if (aiFailed) {
                // silently skip — no toast from modal
            } else {
                setTranslationOptions(result);
                setShowTranslationPicker(true);
                if (!form.translation_mode) {
                    set('translation_mode', 'translation');
                }
            }
        } catch { /* ignore */ }
        finally { setTranslating(false); }
    };

    const handleSave = async () => {
        if (!form.name || !form.category || form.selling_price === '') return;
        // For new products, product_code is optional (backend auto-generates)
        // For edits, it's already set
        if (product && !form.product_code) return;
        setSaving(true);
        try {
            const payload = {
                name: form.name,
                category: form.category,
                // subcategories feature gate: Basic tenants never persist a subcategory
                subcategory: hasFeature('subcategories') ? (form.subcategory || null) : null,
                cost_price: parseFloat(form.cost_price) || 0,
                selling_price: parseFloat(form.selling_price),
                // Blank means 'no second price' — NOT zero, which would
                // sell the product for nothing on a secondary-tier option.
                secondary_price: form.secondary_price === '' || form.secondary_price == null
                    ? null : parseFloat(form.secondary_price),
                // inventory feature gate: Basic tenants default to unlimited/no-tracking
                quantity: hasFeature('inventory') ? (form.infinite_stock ? 0 : parseInt(form.quantity) || 0) : 0,
                description: form.description || null,
                status: form.status || 'active',
                stock_calculation_type: hasFeature('inventory') ? (form.stock_calculation_type || 'manual') : 'manual',
                infinite_stock: hasFeature('inventory') ? (form.infinite_stock || false) : true,
                image_url: form.image_url || null,
                translation_mode: form.translation_mode || null,
                manual_translation: form.manual_translation || null,
            };
            if (form.product_code) {
                payload.product_code = form.product_code;
            }
            await onSave(payload);
            onClose();
        } finally {
            setSaving(false);
        }
    };

    const labelStyle = { fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4, display: 'block' };
    const inputStyle = { padding: '8px 10px', border: '1.5px solid #e2e8f0', borderRadius: 7, fontSize: 13, width: '100%', boxSizing: 'border-box', color: '#212529' };

    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
            <div className="portal-modal-card" style={{ background: '#fff', borderRadius: 12, padding: 28, width: activeTab === 'recipe' || activeTab === 'options' ? 680 : activeTab === 'addons' ? 520 : 520, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.18)', transition: 'width 0.18s ease' }} onClick={e => e.stopPropagation()}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#212529' }}>{product ? `${t('edit_product')} — ${product.name}` : t('add_product')}</h3>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', padding: 4 }}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                    </button>
                </div>

                {/* Tab bar — Details always available; Recipe/legacy Add-ons disabled on
                    create. Options (nested modifier groups) is deliberately NOT gated on
                    recipeAvailable — attaching a group has nothing to do with recipes —
                    but ProductOptionsTab itself shows a "save the product first" message
                    when productCode is still empty (brand-new, unsaved product), so it's
                    safe to leave selectable. The legacy Add-ons tab stays as-is (still
                    gated) so tenants who haven't migrated to modifier groups keep their
                    existing flat add-on lists editable — Options is additive, not a
                    replacement for it. */}
                <div style={{ display: 'flex', gap: 4, borderBottom: '1.5px solid #e2e8f0', marginBottom: 20, flexWrap: 'wrap' }}>
                    {[
                        { key: 'details', label: t('details') },
                        { key: 'recipe',  label: t('recipe'), disabled: !recipeAvailable },
                        { key: 'addons',  label: t('add_ons'), disabled: !recipeAvailable },
                        { key: 'options', label: t('options'), disabled: false },
                    ].map(tab => {
                        const isActive = activeTab === tab.key;
                        return (
                            <button
                                key={tab.key}
                                type="button"
                                onClick={() => !tab.disabled && setActiveTab(tab.key)}
                                disabled={tab.disabled}
                                title={tab.disabled ? t('save_product_first_to_manage_recipe') : undefined}
                                style={{
                                    background: 'none', border: 'none',
                                    padding: '10px 16px', cursor: tab.disabled ? 'not-allowed' : 'pointer',
                                    fontSize: 13, fontWeight: 700,
                                    color: tab.disabled ? '#cbd5e1' : isActive ? '#2563eb' : '#64748b',
                                    borderBottom: isActive ? '2.5px solid #2563eb' : '2.5px solid transparent',
                                    marginBottom: -1.5,
                                    transition: 'color 0.12s',
                                }}
                            >
                                {tab.label}
                            </button>
                        );
                    })}
                </div>

                {activeTab === 'addons' && recipeAvailable ? (
                    <ProductAddonsEditor productCode={product.product_code} productCategory={form.category} t={t} toast={toast} />
                ) : activeTab === 'recipe' && recipeAvailable ? (
                    <ProductRecipeEditor
                        productCode={product.product_code}
                        sellingPrice={form.selling_price}
                        currency={currency}
                        t={t}
                        toast={toast}
                        onRecipeSaved={() => {/* parent refreshes via onSave/onClose cycle; recipe save is independent */}}
                    />
                ) : activeTab === 'options' ? (
                    optionsLoading ? (
                        <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>{t('loading')}</div>
                    ) : (
                        <ProductOptionsTab
                            productCode={product?.product_code}
                            attachedGroupIds={attachedGroupIds}
                            allGroups={allGroups}
                            addons={addons}
                            currency={currency}
                            onSaved={setAttachedGroupIds}
                            t={t}
                            toast={toast}
                        />
                    )
                ) : (
                <>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    <div className="portal-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                        <label>
                            <span style={labelStyle}>{t('product_name')} *</span>
                            <input value={form.name || ''} onChange={e => set('name', e.target.value)} style={inputStyle} placeholder={t('eg_espresso')} />
                        </label>
                        <label>
                            <span style={labelStyle}>{t('product_code')} *</span>
                            <input value={form.product_code || ''} onChange={e => set('product_code', e.target.value)} disabled={!!product} style={{ ...inputStyle, opacity: product ? 0.6 : 1, cursor: product ? 'not-allowed' : 'auto' }} placeholder={t('eg_esp001')} />
                        </label>
                    </div>

                    {/* Translation section */}
                    <div>
                        {form.translation_mode && !showTranslationPicker && (translationOptions || (form.translation_mode === 'manual' && form.manual_translation?.ar)) ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <button type="button" onClick={async () => { setShowTranslationPicker(true); if (!translationOptions) await handleTranslate(); }} disabled={!form.name}
                                    style={{ padding: '4px 10px', borderRadius: 6, border: '1.5px solid #e2e8f0', background: '#f8fafc', color: '#475569', fontSize: 11, fontWeight: 600, cursor: form.name ? 'pointer' : 'not-allowed', opacity: form.name ? 1 : 0.5 }}>
                                    {t('modify_translation')}
                                </button>
                                <span style={{ fontSize: 12, color: '#64748b' }}>
                                    {form.translation_mode === 'manual'
                                        ? `${t('manual_entry')}: ${form.manual_translation?.ar || '\u2014'}`
                                        : form.translation_mode === 'translation'
                                            ? `${t('translation')}: ${translationOptions?.translation || '\u2014'}`
                                            : `${t('transliteration')}: ${translationOptions?.transliteration || '\u2014'}`
                                    }
                                </span>
                            </div>
                        ) : !showTranslationPicker ? (
                            <button type="button" onClick={handleTranslate} disabled={translating || !form.name}
                                style={{ padding: '4px 10px', borderRadius: 6, border: '1.5px solid #bfdbfe', background: '#eff5ff', color: '#1d4ed8', fontSize: 11, fontWeight: 600, cursor: (translating || !form.name) ? 'not-allowed' : 'pointer', opacity: (translating || !form.name) ? 0.5 : 1 }}>
                                {translating ? `${t('translating')}...` : t('translate')}
                            </button>
                        ) : null}

                        {showTranslationPicker && translationOptions?.suggestion && translationOptions.suggestion !== form.name && (
                            <div style={{ marginTop: 6, padding: '6px 10px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, fontSize: 11, color: '#92400e', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <span>{t('did_you_mean')} <strong>"{translationOptions.suggestion}"</strong>?</span>
                                <button type="button" onClick={() => set('name', translationOptions.suggestion)} style={{ background: 'none', border: 'none', textDecoration: 'underline', color: '#92400e', cursor: 'pointer', fontSize: 11 }}>{t('use_this')}</button>
                            </div>
                        )}

                        {showTranslationPicker && translationOptions && (
                            <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
                                {translationOptions.translation && (
                                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', border: '1.5px solid', borderColor: form.translation_mode === 'translation' ? '#2563eb' : '#e2e8f0', borderRadius: 7, cursor: 'pointer', background: form.translation_mode === 'translation' ? '#eff5ff' : '#fff' }}>
                                        <input type="radio" name="translation_mode" checked={form.translation_mode === 'translation'} onChange={() => setForm(f => ({ ...f, translation_mode: 'translation', manual_translation: null }))} style={{ accentColor: '#2563eb' }} />
                                        <span style={{ fontSize: 12, color: '#475569' }}>{t('translation')}:</span>
                                        <span style={{ fontSize: 12, fontWeight: 600 }} dir="rtl">{translationOptions.translation}</span>
                                    </label>
                                )}
                                {translationOptions.transliteration && (
                                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', border: '1.5px solid', borderColor: form.translation_mode === 'transliteration' ? '#2563eb' : '#e2e8f0', borderRadius: 7, cursor: 'pointer', background: form.translation_mode === 'transliteration' ? '#eff5ff' : '#fff' }}>
                                        <input type="radio" name="translation_mode" checked={form.translation_mode === 'transliteration'} onChange={() => setForm(f => ({ ...f, translation_mode: 'transliteration', manual_translation: null }))} style={{ accentColor: '#2563eb' }} />
                                        <span style={{ fontSize: 12, color: '#475569' }}>{t('transliteration')}:</span>
                                        <span style={{ fontSize: 12, fontWeight: 600 }} dir="rtl">{translationOptions.transliteration}</span>
                                    </label>
                                )}
                                {translationOptions.community_manuals?.length > 0 && (
                                    <div style={{ paddingLeft: 8 }}>
                                        <p style={{ fontSize: 10, color: '#94a3b8', marginBottom: 4 }}>{t('community_suggestions')}</p>
                                        {translationOptions.community_manuals.map((m, i) => (
                                            <label key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 10px', border: '1.5px solid', borderColor: (form.translation_mode === 'manual' && form.manual_translation?.ar === m) ? '#2563eb' : '#e2e8f0', borderRadius: 7, cursor: 'pointer', background: (form.translation_mode === 'manual' && form.manual_translation?.ar === m) ? '#eff5ff' : '#fff', marginBottom: 4 }}>
                                                <input type="radio" name="translation_mode" checked={form.translation_mode === 'manual' && form.manual_translation?.ar === m} onChange={() => setForm(f => ({ ...f, translation_mode: 'manual', manual_translation: { ar: m } }))} style={{ accentColor: '#2563eb' }} />
                                                <span style={{ fontSize: 12, fontWeight: 600 }} dir="rtl">{m}</span>
                                            </label>
                                        ))}
                                    </div>
                                )}
                                <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', border: '1.5px solid', borderColor: form.translation_mode === 'manual' ? '#2563eb' : '#e2e8f0', borderRadius: 7, cursor: 'pointer', background: form.translation_mode === 'manual' ? '#eff5ff' : '#fff' }}>
                                    <input type="radio" name="translation_mode" checked={form.translation_mode === 'manual'} onChange={() => setForm(f => ({ ...f, translation_mode: 'manual', manual_translation: f.manual_translation || { ar: '' } }))} style={{ accentColor: '#2563eb' }} />
                                    <span style={{ fontSize: 12, color: '#475569' }}>{t('manual_entry')}</span>
                                </label>
                                {form.translation_mode === 'manual' && (
                                    <input type="text" dir="rtl" placeholder={t('enter_arabic_name')} value={form.manual_translation?.ar || ''} onChange={e => setForm(f => ({ ...f, manual_translation: { ar: e.target.value } }))}
                                        style={{ ...inputStyle, textAlign: 'right' }} />
                                )}
                                {form.translation_mode && (
                                    <button type="button" onClick={() => {
                                        setShowTranslationPicker(false);
                                    }} disabled={form.translation_mode === 'manual' && !form.manual_translation?.ar}
                                        style={{ marginTop: 4, padding: '7px 16px', borderRadius: 7, border: 'none', background: '#2563eb', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer', alignSelf: 'flex-start', opacity: (saving || (form.translation_mode === 'manual' && !form.manual_translation?.ar)) ? 0.5 : 1 }}>
                                        {saving ? `${t('saving')}...` : t('save_translation')}
                                    </button>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="portal-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                        <div>
                            <span style={labelStyle}>{t('category')} *</span>
                            {creatingCategory ? (
                                <div style={{ display: 'flex', gap: 6 }}>
                                    <input
                                        autoFocus
                                        value={newCategoryName}
                                        onChange={e => setNewCategoryName(e.target.value)}
                                        onKeyDown={e => { if (e.key === 'Escape') { setCreatingCategory(false); setNewCategoryName(''); } }}
                                        style={{ ...inputStyle, flex: 1 }}
                                        placeholder={t('category_name')}
                                    />
                                    <button
                                        type="button"
                                        disabled={!newCategoryName.trim()}
                                        onClick={async () => {
                                            try {
                                                const created = await categoryService.create({ name: newCategoryName.trim() });
                                                const refreshed = await categoryService.getAll();
                                                setCategories(refreshed);
                                                setForm(f => ({ ...f, category: created.name, subcategory: '' }));
                                                setCreatingCategory(false);
                                                setNewCategoryName('');
                                            } catch { toast(t('error_creating_category'), 'error'); }
                                        }}
                                        style={{ padding: '6px 12px', borderRadius: 7, border: 'none', background: newCategoryName.trim() ? '#2563eb' : '#94a3b8', color: '#fff', fontSize: 12, fontWeight: 600, cursor: newCategoryName.trim() ? 'pointer' : 'not-allowed', whiteSpace: 'nowrap' }}
                                    >
                                        {t('create')}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => { setCreatingCategory(false); setNewCategoryName(''); }}
                                        style={{ padding: '6px 10px', borderRadius: 7, border: '1.5px solid #e2e8f0', background: '#fff', color: '#64748b', fontSize: 12, fontWeight: 500, cursor: 'pointer', whiteSpace: 'nowrap' }}
                                    >
                                        {t('cancel')}
                                    </button>
                                </div>
                            ) : (
                                <>
                                    <select
                                        value={form.category || ''}
                                        onChange={e => {
                                            if (e.target.value === '__add_new__') { setCreatingCategory(true); return; }
                                            setForm(f => ({ ...f, category: e.target.value, subcategory: '' }));
                                        }}
                                        style={inputStyle}
                                    >
                                        <option value="">{t('select_category')}</option>
                                        {categories.map(cat => (
                                            <option key={cat.id} value={cat.name}>{cat.name}</option>
                                        ))}
                                        <option value="__add_new__">+ {t('add_category')}</option>
                                    </select>
                                    {categories.length === 0 && (
                                        <p style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
                                            {t('no_categories_configured')}
                                        </p>
                                    )}
                                </>
                            )}
                        </div>
                        {hasFeature('subcategories') && (
                        <div>
                            <span style={labelStyle}>{t('subcategory')}</span>
                            {creatingSub ? (
                                <div style={{ display: 'flex', gap: 6 }}>
                                    <input
                                        autoFocus
                                        value={newSubName}
                                        onChange={e => setNewSubName(e.target.value)}
                                        onKeyDown={e => { if (e.key === 'Escape') { setCreatingSub(false); setNewSubName(''); } }}
                                        style={{ ...inputStyle, flex: 1 }}
                                        placeholder={t('subcategory_name')}
                                    />
                                    <button
                                        type="button"
                                        disabled={!newSubName.trim()}
                                        onClick={async () => {
                                            const selectedCat = categories.find(c => c.name === form.category);
                                            if (!selectedCat) { toast(t('select_category_first'), 'error'); return; }
                                            try {
                                                const created = await categoryService.createSubcategory({ name: newSubName.trim(), category_id: selectedCat.id });
                                                const refreshed = await categoryService.getSubcategories(selectedCat.id);
                                                setSubcategories(refreshed);
                                                setForm(f => ({ ...f, subcategory: created.name }));
                                                setCreatingSub(false);
                                                setNewSubName('');
                                            } catch { toast(t('error_creating_subcategory'), 'error'); }
                                        }}
                                        style={{ padding: '6px 12px', borderRadius: 7, border: 'none', background: newSubName.trim() ? '#2563eb' : '#94a3b8', color: '#fff', fontSize: 12, fontWeight: 600, cursor: newSubName.trim() ? 'pointer' : 'not-allowed', whiteSpace: 'nowrap' }}
                                    >
                                        {t('create')}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => { setCreatingSub(false); setNewSubName(''); }}
                                        style={{ padding: '6px 10px', borderRadius: 7, border: '1.5px solid #e2e8f0', background: '#fff', color: '#64748b', fontSize: 12, fontWeight: 500, cursor: 'pointer', whiteSpace: 'nowrap' }}
                                    >
                                        {t('cancel')}
                                    </button>
                                </div>
                            ) : (
                                <select
                                    value={form.subcategory || ''}
                                    onChange={e => {
                                        if (e.target.value === '__add_new__') {
                                            if (!form.category) { toast(t('select_category_first'), 'error'); return; }
                                            setCreatingSub(true);
                                            return;
                                        }
                                        setForm(f => ({ ...f, subcategory: e.target.value }));
                                    }}
                                    style={inputStyle}
                                    disabled={!form.category}
                                >
                                    <option value="">{t('select_subcategory')}</option>
                                    {subcategories.map(sub => (
                                        <option key={sub.id} value={sub.name}>{sub.name}</option>
                                    ))}
                                    <option value="__add_new__">+ {t('add_subcategory')}</option>
                                </select>
                            )}
                        </div>
                        )}
                    </div>

                    <div className="portal-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                        <label>
                            <span style={labelStyle}>{t('selling_price')} * ({currency})</span>
                            <input type="number" min="0" step="0.01" value={form.selling_price ?? ''} onChange={e => set('selling_price', e.target.value)} style={inputStyle} placeholder="0.00" />
                        </label>
                        <label>
                            {/* Optional. A dining option set to the secondary
                                tier still sells at the primary price when this
                                is blank, so only fill it in for products that
                                actually differ (e.g. delivery). */}
                            <span style={labelStyle}>{t('secondary_price')} ({currency})</span>
                            <input
                                type="number" min="0" step="0.01"
                                data-testid="secondary-price"
                                value={form.secondary_price ?? ''}
                                onChange={e => set('secondary_price', e.target.value)}
                                style={inputStyle}
                                placeholder={t('same_as_selling_price')}
                            />
                        </label>
                        <label>
                            <span style={labelStyle}>{t('cost_price')} ({currency})</span>
                            <input
                                type="number" min="0" step="0.01"
                                value={form.cost_price ?? ''}
                                onChange={e => set('cost_price', e.target.value)}
                                style={{
                                    ...inputStyle,
                                    ...(form.stock_calculation_type === 'auto' || product?.cost_source === 'recipe' ? { background: '#f1f5f9', color: '#64748b', cursor: 'not-allowed' } : {}),
                                }}
                                placeholder="0.00"
                                disabled={form.stock_calculation_type === 'auto' || product?.cost_source === 'recipe'}
                                title={form.stock_calculation_type === 'auto' ? t('cost_auto_calculated') : product?.cost_source === 'recipe' ? t('cost_source_recipe_tooltip') : undefined}
                            />
                            {product?.cost_source === 'recipe' && product?.effective_cost_price != null && (
                                <div style={{ marginTop: 4, fontSize: 11, color: '#166534' }}>
                                    {t('cost_source_recipe')}: {currency}{Number(product.effective_cost_price).toFixed(2)}
                                </div>
                            )}
                            {product?.cost_source === 'recipe_partial' && (
                                <div style={{ marginTop: 4, fontSize: 11, color: '#d97706' }}>
                                    {t('cost_source_recipe_partial')}
                                </div>
                            )}
                            {product?.cost_source === 'unknown' && product?.cost_warnings?.length > 0 && (
                                <div style={{ marginTop: 4, fontSize: 11, color: '#dc2626' }}>
                                    {product.cost_warnings.join(' · ')}
                                </div>
                            )}
                        </label>
                    </div>

                    {/* Icon / Image — placed above Stock so the visual asset sits with the
                        product-definition fields, and Stock Management stays compact below. */}
                    <div>
                        <span style={labelStyle}>{t('icon_picker_label')}</span>
                        <IconPicker
                            value={form.image_url}
                            onChange={(val) => set('image_url', val)}
                            entityType="product"
                            entityId={product?.product_code}
                        />
                    </div>

                    {/* Stock section — horizontal radio pills
                        Three exclusive modes derived from the two DB flags:
                          Limited      → infinite_stock=false, stock_type=manual (shows quantity)
                          Unlimited    → infinite_stock=true
                          Auto-calc'd  → infinite_stock=false, stock_type=auto   (derives qty from recipe)
                        For Auto mode we surface a direct path to the Recipe tab when the product
                        has no recipe yet, so the user isn't left guessing why stock isn't calculating.
                        Gated: only rendered when tenant has the 'inventory' feature. */}
                    {hasFeature('inventory') && (() => {
                        const stockMode = form.infinite_stock
                            ? 'unlimited'
                            : form.stock_calculation_type === 'auto' ? 'auto' : 'limited';
                        const hasRecipe = product?.cost_source === 'recipe' || product?.cost_source === 'recipe_partial';
                        const setStockMode = (m) => {
                            if (m === 'unlimited') setForm(f => ({ ...f, infinite_stock: true, stock_calculation_type: 'manual' }));
                            else if (m === 'auto') setForm(f => ({ ...f, infinite_stock: false, stock_calculation_type: 'auto' }));
                            else setForm(f => ({ ...f, infinite_stock: false, stock_calculation_type: 'manual' }));
                        };
                        const modes = [
                            { key: 'limited',   label: t('limited_stock') },
                            { key: 'unlimited', label: t('unlimited_stock') },
                            { key: 'auto',      label: t('auto_stock') },
                        ];
                        return (
                            <div>
                                <span style={labelStyle}>{t('stock_management')}</span>
                                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                                    {modes.map(m => {
                                        const selected = stockMode === m.key;
                                        return (
                                            <label
                                                key={m.key}
                                                onClick={() => setStockMode(m.key)}
                                                style={{
                                                    display: 'inline-flex', alignItems: 'center', gap: 7,
                                                    padding: '7px 12px', borderRadius: 7,
                                                    border: '1.5px solid ' + (selected ? '#2563eb' : '#e2e8f0'),
                                                    background: selected ? '#eff5ff' : '#fff',
                                                    cursor: 'pointer',
                                                    fontSize: 12, fontWeight: 600,
                                                    color: selected ? '#1d4ed8' : '#475569',
                                                    transition: 'all 0.12s',
                                                    userSelect: 'none',
                                                }}
                                            >
                                                <span style={{
                                                    width: 14, height: 14, borderRadius: '50%',
                                                    border: '2px solid ' + (selected ? '#2563eb' : '#cbd5e1'),
                                                    background: '#fff',
                                                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                                    flexShrink: 0,
                                                }}>
                                                    {selected && <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#2563eb' }} />}
                                                </span>
                                                {m.label}
                                            </label>
                                        );
                                    })}
                                    {stockMode === 'limited' && (
                                        <input
                                            type="number" min="0"
                                            value={form.quantity ?? 0}
                                            onChange={e => set('quantity', e.target.value)}
                                            placeholder={t('quantity')}
                                            style={{ ...inputStyle, width: 120, marginLeft: 'auto' }}
                                        />
                                    )}
                                </div>
                                {stockMode === 'auto' && !hasRecipe && (
                                    <div style={{
                                        marginTop: 8, padding: '8px 10px',
                                        background: '#fffbeb', border: '1px solid #fde68a',
                                        borderRadius: 6, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
                                    }}>
                                        <span style={{ fontSize: 11, color: '#92400e', flex: 1, minWidth: 140 }}>
                                            {recipeAvailable ? t('auto_needs_recipe') : t('auto_save_first_to_build_recipe')}
                                        </span>
                                        {recipeAvailable && (
                                            <button
                                                type="button"
                                                onClick={() => setActiveTab('recipe')}
                                                style={{
                                                    padding: '4px 10px', borderRadius: 5,
                                                    border: '1.5px solid #d97706',
                                                    background: '#fff', color: '#d97706',
                                                    fontSize: 11, fontWeight: 700, cursor: 'pointer',
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                {t('manage_recipe')} →
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })()}

                    <label>
                        <span style={labelStyle}>{t('description')}</span>
                        <textarea value={form.description || ''} onChange={e => set('description', e.target.value)} rows={2} style={{ ...inputStyle, resize: 'vertical' }} placeholder={t('optional_description')} />
                    </label>

                    <label>
                        <span style={labelStyle}>{t('status')}</span>
                        <select value={form.status || 'active'} onChange={e => set('status', e.target.value)} style={inputStyle}>
                            <option value="active">{t('active')}</option>
                            <option value="inactive">{t('inactive')}</option>
                        </select>
                    </label>
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 22 }}>
                    <button onClick={onClose} style={{ padding: '8px 16px', borderRadius: 7, border: '1.5px solid #e2e8f0', background: '#fff', color: '#64748b', fontSize: 13, fontWeight: 500, cursor: 'pointer' }}>{t('cancel')}</button>
                    <button onClick={handleSave} disabled={saving || !form.name || !form.product_code || !form.category || form.selling_price === ''}
                        style={{ padding: '8px 20px', borderRadius: 7, border: 'none', background: (form.name && form.product_code && form.category && form.selling_price !== '') ? '#2563eb' : '#94a3b8', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                        {saving ? t('saving') : product ? t('save_changes') : t('add_product')}
                    </button>
                </div>
                </>
                )}
            </div>
        </div>
    );
};

// ─── Addon Modal ───────────────────────────────────────────────────────────────
const AddonModal = ({ open, onClose, onSave, addon, currency, t, translationDetails, toast }) => {
    const [form, setForm] = useState({});
    const [saving, setSaving] = useState(false);
    const [translating, setTranslating] = useState(false);
    const [translationOptions, setTranslationOptions] = useState(null);
    const [showTranslationPicker, setShowTranslationPicker] = useState(false);
    const [addonCategories, setAddonCategories] = useState([]);
    const [creatingAddonCategory, setCreatingAddonCategory] = useState(false);
    const [newAddonCategoryName, setNewAddonCategoryName] = useState('');

    useEffect(() => {
        if (open) categoryService.getAll().then(setAddonCategories).catch(console.error);
    }, [open]);

    const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
    const categoryDropdownRef = React.useRef(null);

    // Close category dropdown on outside click
    useEffect(() => {
        if (!categoryDropdownOpen) return;
        const onDoc = (e) => { if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(e.target)) setCategoryDropdownOpen(false); };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, [categoryDropdownOpen]);

    useEffect(() => {
        if (open) {
            // Normalize category to an array
            const rawCat = addon?.category;
            const catArray = Array.isArray(rawCat) ? rawCat : (typeof rawCat === 'string' && rawCat.startsWith('[') ? (() => { try { return JSON.parse(rawCat); } catch { return rawCat ? [rawCat] : []; } })() : rawCat ? [rawCat] : []);
            setForm(addon ? {
                name: addon.name || '', category: catArray, price: addon.price ?? '', description: addon.description || '',
                translation_mode: addon.translation_mode || null, manual_translation: addon.manual_translation || null,
            } : { name: '', category: [], price: '', description: '', translation_mode: null, manual_translation: null });
            setCategoryDropdownOpen(false);
            if (addon?.translation_mode && translationDetails?.[addon.name]) {
                const ar = translationDetails[addon.name]?.ar || {};
                setTranslationOptions({ translation: ar.translation || null, transliteration: ar.transliteration || null, community_manuals: ar.community_manuals || [], suggestion: null });
            } else {
                setTranslationOptions(null);
            }
            setShowTranslationPicker(false);
            setCreatingAddonCategory(false);
            setNewAddonCategoryName('');
        }
    }, [open, addon, translationDetails]);

    useEffect(() => {
        const onKey = e => { if (e.key === 'Escape') onClose(); };
        if (open) window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, onClose]);

    if (!open) return null;

    const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
    const inputStyle = { padding: '8px 10px', border: '1.5px solid #e2e8f0', borderRadius: 7, fontSize: 13, width: '100%', boxSizing: 'border-box', color: '#212529' };
    const labelStyle = { fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4, display: 'block' };

    const handleAddonTranslate = async () => {
        if (!form.name) return;
        setTranslating(true);
        try {
            const catContext = Array.isArray(form.category) ? (form.category[0] || 'addon') : (form.category || 'addon');
            const result = await translationService.translateProductName(form.name, catContext);
            const aiFailed = result.translation === form.name && result.transliteration === form.name;
            if (!aiFailed) {
                setTranslationOptions(result);
                setShowTranslationPicker(true);
                if (!form.translation_mode) set('translation_mode', 'translation');
            }
        } catch { /* ignore */ }
        finally { setTranslating(false); }
    };

    const handleSave = async () => {
        if (!form.name || !form.category || form.category.length === 0 || form.price === '') return;
        setSaving(true);
        try { await onSave({ ...form, category: form.category, price: parseFloat(form.price) }); onClose(); }
        finally { setSaving(false); }
    };

    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
            <div className="portal-modal-card" style={{ background: '#fff', borderRadius: 12, padding: 28, width: 420, boxShadow: '0 20px 60px rgba(0,0,0,0.18)' }} onClick={e => e.stopPropagation()}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                    <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#212529' }}>{addon ? t('edit_addon') : t('add_add_on')}</h3>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', padding: 4 }}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                    </button>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    <label><span style={labelStyle}>{t('name')} *</span><input value={form.name || ''} onChange={e => set('name', e.target.value)} style={inputStyle} placeholder={t('eg_extra_shot')} /></label>

                    {/* Translation section */}
                    <div>
                        {form.translation_mode && !showTranslationPicker && (translationOptions || (form.translation_mode === 'manual' && form.manual_translation?.ar)) ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <button type="button" onClick={async () => { setShowTranslationPicker(true); if (!translationOptions) await handleAddonTranslate(); }} disabled={!form.name}
                                    style={{ padding: '4px 10px', borderRadius: 6, border: '1.5px solid #e2e8f0', background: '#f8fafc', color: '#475569', fontSize: 11, fontWeight: 600, cursor: form.name ? 'pointer' : 'not-allowed', opacity: form.name ? 1 : 0.5 }}>
                                    {t('modify_translation')}
                                </button>
                                <span style={{ fontSize: 12, color: '#64748b' }}>
                                    {form.translation_mode === 'manual'
                                        ? `${t('manual_entry')}: ${form.manual_translation?.ar || '\u2014'}`
                                        : form.translation_mode === 'translation'
                                            ? `${t('translation')}: ${translationOptions?.translation || '\u2014'}`
                                            : `${t('transliteration')}: ${translationOptions?.transliteration || '\u2014'}`
                                    }
                                </span>
                            </div>
                        ) : !showTranslationPicker ? (
                            <button type="button" onClick={handleAddonTranslate} disabled={translating || !form.name}
                                style={{ padding: '4px 10px', borderRadius: 6, border: '1.5px solid #bfdbfe', background: '#eff5ff', color: '#1d4ed8', fontSize: 11, fontWeight: 600, cursor: (translating || !form.name) ? 'not-allowed' : 'pointer', opacity: (translating || !form.name) ? 0.5 : 1 }}>
                                {translating ? `${t('translating')}...` : t('translate')}
                            </button>
                        ) : null}

                        {showTranslationPicker && translationOptions?.suggestion && translationOptions.suggestion !== form.name && (
                            <div style={{ marginTop: 6, padding: '6px 10px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, fontSize: 11, color: '#92400e', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <span>{t('did_you_mean')} <strong>"{translationOptions.suggestion}"</strong>?</span>
                                <button type="button" onClick={() => set('name', translationOptions.suggestion)} style={{ background: 'none', border: 'none', textDecoration: 'underline', color: '#92400e', cursor: 'pointer', fontSize: 11 }}>{t('use_this')}</button>
                            </div>
                        )}

                        {showTranslationPicker && translationOptions && (
                            <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
                                {translationOptions.translation && (
                                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', border: '1.5px solid', borderColor: form.translation_mode === 'translation' ? '#2563eb' : '#e2e8f0', borderRadius: 7, cursor: 'pointer', background: form.translation_mode === 'translation' ? '#eff5ff' : '#fff' }}>
                                        <input type="radio" name="addon_translation_mode" checked={form.translation_mode === 'translation'} onChange={() => setForm(f => ({ ...f, translation_mode: 'translation', manual_translation: null }))} style={{ accentColor: '#2563eb' }} />
                                        <span style={{ fontSize: 12, color: '#475569' }}>{t('translation')}:</span>
                                        <span style={{ fontSize: 12, fontWeight: 600 }} dir="rtl">{translationOptions.translation}</span>
                                    </label>
                                )}
                                {translationOptions.transliteration && (
                                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', border: '1.5px solid', borderColor: form.translation_mode === 'transliteration' ? '#2563eb' : '#e2e8f0', borderRadius: 7, cursor: 'pointer', background: form.translation_mode === 'transliteration' ? '#eff5ff' : '#fff' }}>
                                        <input type="radio" name="addon_translation_mode" checked={form.translation_mode === 'transliteration'} onChange={() => setForm(f => ({ ...f, translation_mode: 'transliteration', manual_translation: null }))} style={{ accentColor: '#2563eb' }} />
                                        <span style={{ fontSize: 12, color: '#475569' }}>{t('transliteration')}:</span>
                                        <span style={{ fontSize: 12, fontWeight: 600 }} dir="rtl">{translationOptions.transliteration}</span>
                                    </label>
                                )}
                                {translationOptions.community_manuals?.length > 0 && (
                                    <div style={{ paddingLeft: 8 }}>
                                        <p style={{ fontSize: 10, color: '#94a3b8', marginBottom: 4 }}>{t('community_suggestions')}</p>
                                        {translationOptions.community_manuals.map((m, i) => (
                                            <label key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 10px', border: '1.5px solid', borderColor: (form.translation_mode === 'manual' && form.manual_translation?.ar === m) ? '#2563eb' : '#e2e8f0', borderRadius: 7, cursor: 'pointer', background: (form.translation_mode === 'manual' && form.manual_translation?.ar === m) ? '#eff5ff' : '#fff', marginBottom: 4 }}>
                                                <input type="radio" name="addon_translation_mode" checked={form.translation_mode === 'manual' && form.manual_translation?.ar === m} onChange={() => setForm(f => ({ ...f, translation_mode: 'manual', manual_translation: { ar: m } }))} style={{ accentColor: '#2563eb' }} />
                                                <span style={{ fontSize: 12, fontWeight: 600 }} dir="rtl">{m}</span>
                                            </label>
                                        ))}
                                    </div>
                                )}
                                <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', border: '1.5px solid', borderColor: form.translation_mode === 'manual' ? '#2563eb' : '#e2e8f0', borderRadius: 7, cursor: 'pointer', background: form.translation_mode === 'manual' ? '#eff5ff' : '#fff' }}>
                                    <input type="radio" name="addon_translation_mode" checked={form.translation_mode === 'manual'} onChange={() => setForm(f => ({ ...f, translation_mode: 'manual', manual_translation: f.manual_translation || { ar: '' } }))} style={{ accentColor: '#2563eb' }} />
                                    <span style={{ fontSize: 12, color: '#475569' }}>{t('manual_entry')}</span>
                                </label>
                                {form.translation_mode === 'manual' && (
                                    <input type="text" dir="rtl" placeholder={t('enter_arabic_name')} value={form.manual_translation?.ar || ''} onChange={e => setForm(f => ({ ...f, manual_translation: { ar: e.target.value } }))}
                                        style={{ ...inputStyle, textAlign: 'right' }} />
                                )}
                                {form.translation_mode && (
                                    <button type="button" onClick={() => {
                                        setShowTranslationPicker(false);
                                    }} disabled={form.translation_mode === 'manual' && !form.manual_translation?.ar}
                                        style={{ marginTop: 4, padding: '7px 16px', borderRadius: 7, border: 'none', background: '#2563eb', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer', alignSelf: 'flex-start', opacity: (saving || (form.translation_mode === 'manual' && !form.manual_translation?.ar)) ? 0.5 : 1 }}>
                                        {saving ? `${t('saving')}...` : t('save_translation')}
                                    </button>
                                )}
                            </div>
                        )}
                    </div>

                    <div>
                        <span style={labelStyle}>{t('category')} *</span>
                        {creatingAddonCategory ? (
                            <div style={{ display: 'flex', gap: 6 }}>
                                <input
                                    autoFocus
                                    value={newAddonCategoryName}
                                    onChange={e => setNewAddonCategoryName(e.target.value)}
                                    onKeyDown={e => { if (e.key === 'Escape') { setCreatingAddonCategory(false); setNewAddonCategoryName(''); } }}
                                    style={{ ...inputStyle, flex: 1 }}
                                    placeholder={t('category_name')}
                                />
                                <button
                                    type="button"
                                    disabled={!newAddonCategoryName.trim()}
                                    onClick={async () => {
                                        try {
                                            const created = await categoryService.create({ name: newAddonCategoryName.trim() });
                                            const refreshed = await categoryService.getAll();
                                            setAddonCategories(refreshed);
                                            setForm(f => ({ ...f, category: [...(f.category || []), created.name] }));
                                            setCreatingAddonCategory(false);
                                            setNewAddonCategoryName('');
                                        } catch { toast(t('error_creating_category'), 'error'); }
                                    }}
                                    style={{ padding: '6px 12px', borderRadius: 7, border: 'none', background: newAddonCategoryName.trim() ? '#2563eb' : '#94a3b8', color: '#fff', fontSize: 12, fontWeight: 600, cursor: newAddonCategoryName.trim() ? 'pointer' : 'not-allowed', whiteSpace: 'nowrap' }}
                                >
                                    {t('create')}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => { setCreatingAddonCategory(false); setNewAddonCategoryName(''); }}
                                    style={{ padding: '6px 10px', borderRadius: 7, border: '1.5px solid #e2e8f0', background: '#fff', color: '#64748b', fontSize: 12, fontWeight: 500, cursor: 'pointer', whiteSpace: 'nowrap' }}
                                >
                                    {t('cancel')}
                                </button>
                            </div>
                        ) : (
                            <div ref={categoryDropdownRef} style={{ position: 'relative' }}>
                                <button type="button" onClick={() => setCategoryDropdownOpen(o => !o)} style={{ ...inputStyle, textAlign: 'left', cursor: 'pointer', background: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {form.category && form.category.length > 0 ? form.category.join(', ') : t('select_categories')}
                                    <span style={{ float: 'right', color: '#94a3b8', fontSize: 10 }}>&#9660;</span>
                                </button>
                                {categoryDropdownOpen && (
                                    <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4, background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.08)', zIndex: 50, maxHeight: 220, overflowY: 'auto' }}>
                                        {addonCategories.map(cat => {
                                            const checked = (form.category || []).includes(cat.name);
                                            return (
                                                <label key={cat.id} onClick={(e) => { e.preventDefault(); setForm(f => { const cats = f.category || []; return { ...f, category: checked ? cats.filter(c => c !== cat.name) : [...cats, cat.name] }; }); }}
                                                    style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 12px', cursor: 'pointer', fontSize: 12, color: '#212529', background: checked ? '#eff5ff' : '#fff' }}
                                                    onMouseEnter={e => { if (!checked) e.currentTarget.style.background = '#f8fafc'; }}
                                                    onMouseLeave={e => { if (!checked) e.currentTarget.style.background = '#fff'; }}
                                                >
                                                    <input type="checkbox" checked={checked} readOnly style={{ accentColor: '#2563eb', cursor: 'pointer' }} />
                                                    {cat.name}
                                                </label>
                                            );
                                        })}
                                        <div onClick={() => { setCreatingAddonCategory(true); setCategoryDropdownOpen(false); }}
                                            style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: '#2563eb', borderTop: '1px solid #f1f5f9' }}
                                            onMouseEnter={e => { e.currentTarget.style.background = '#eff5ff'; }}
                                            onMouseLeave={e => { e.currentTarget.style.background = '#fff'; }}
                                        >
                                            + {t('add_category')}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    <label><span style={labelStyle}>{t('price')} * ({currency})</span><input type="number" min="0" step="0.01" value={form.price ?? ''} onChange={e => set('price', e.target.value)} style={inputStyle} placeholder="0.00" /></label>
                    <label><span style={labelStyle}>{t('description')}</span><input value={form.description || ''} onChange={e => set('description', e.target.value)} style={inputStyle} placeholder={t('optional')} /></label>
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
                    <button onClick={onClose} style={{ padding: '8px 16px', borderRadius: 7, border: '1.5px solid #e2e8f0', background: '#fff', color: '#64748b', fontSize: 13, fontWeight: 500, cursor: 'pointer' }}>{t('cancel')}</button>
                    <button onClick={handleSave} disabled={saving || !form.name || !form.category || form.category.length === 0 || form.price === ''}
                        style={{ padding: '8px 20px', borderRadius: 7, border: 'none', background: (form.name && form.category && form.category.length > 0 && form.price !== '') ? '#2563eb' : '#94a3b8', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                        {saving ? t('saving') : addon ? t('save_changes') : t('add_add_on')}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ─── Delete Modal ──────────────────────────────────────────────────────────────
const DeleteModal = ({ open, onClose, onConfirm, name, isDeactivate, t }) => {
    const [reason, setReason] = useState('');
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (open) setReason('');
        const onKey = e => { if (e.key === 'Escape') onClose(); };
        if (open) window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, onClose]);

    if (!open) return null;

    const handleConfirm = async () => {
        setBusy(true);
        try { await onConfirm(reason); onClose(); } finally { setBusy(false); }
    };

    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
            <div className="portal-modal-card" style={{ background: '#fff', borderRadius: 12, padding: 28, width: 380, boxShadow: '0 20px 60px rgba(0,0,0,0.18)' }} onClick={e => e.stopPropagation()}>
                <div style={{ width: 44, height: 44, borderRadius: '50%', background: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/></svg>
                </div>
                <h3 style={{ margin: '0 0 6px', fontSize: 15, fontWeight: 700, color: '#212529', textAlign: 'center' }}>{isDeactivate ? t('deactivate_product') : t('delete')}</h3>
                <p style={{ margin: '0 0 16px', fontSize: 13, color: '#64748b', textAlign: 'center' }}>
                    {isDeactivate ? <>{t('deactivate')} <strong>{name}</strong>? {t('hidden_from_cashier')}</> : <>{t('permanently_delete')} <strong>{name}</strong>?</>}
                </p>
                {isDeactivate && (
                    <textarea value={reason} onChange={e => setReason(e.target.value)} placeholder={t('reason_optional')} rows={2}
                        style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', border: '1.5px solid #e2e8f0', borderRadius: 7, fontSize: 13, marginBottom: 14, resize: 'vertical' }} />
                )}
                <div style={{ display: 'flex', gap: 10 }}>
                    <button onClick={onClose} style={{ flex: 1, padding: '9px 0', borderRadius: 7, border: '1.5px solid #e2e8f0', background: '#fff', color: '#64748b', fontSize: 13, fontWeight: 500, cursor: 'pointer' }}>{t('cancel')}</button>
                    <button onClick={handleConfirm} disabled={busy} style={{ flex: 1, padding: '9px 0', borderRadius: 7, border: 'none', background: '#dc2626', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                        {busy ? t('please_wait') : isDeactivate ? t('deactivate') : t('delete')}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ─── Main Page ────────────────────────────────────────────────────────────────
const PortalProductsPage = () => {
    const { getCurrency } = useAuth();
    const { t, translationDetails } = useLanguage();
    const currency = getCurrency();
    const isMobile = useIsMobile();

    // Lightweight toast used by the ProductRecipeEditor inside ProductModal.
    // Keeps the recipe editor feedback on the page it lives on rather than
    // bubbling up to the parent's success/error banner pattern.
    const [toastMsg, setToastMsg] = useState(null);
    const toast = useCallback((msg, type = 'success') => {
        setToastMsg({ msg: String(msg), type });
        setTimeout(() => setToastMsg(null), 3500);
    }, []);

    const [tab, setTab] = useState('products');
    const [products, setProducts] = useState([]);
    const [bulkCalculatedStock, setBulkCalculatedStock] = useState({});
    const [addons, setAddons] = useState([]);
    const [modifierGroups, setModifierGroups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // Products UI state
    const [viewMode, setViewMode] = useState('table');
    const [search, setSearch] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('All');
    const [includeInactive, setIncludeInactive] = useState(false);
    const [stockFilter, setStockFilter] = useState('all');

    // Modals
    const [productModal, setProductModal] = useState({ open: false, product: null });
    const [addonModal, setAddonModal] = useState({ open: false, addon: null });
    const [deleteModal, setDeleteModal] = useState({ open: false, item: null, type: null, isDeactivate: false });
    const [showBulkAdd, setShowBulkAdd] = useState(false);
    const [showRecipeImport, setShowRecipeImport] = useState(false);

    // Bulk selection
    const [selectedProducts, setSelectedProducts] = useState([]);

    // ── Load ───────────────────────────────────────────────────────────────────
    const loadProducts = useCallback(async () => {
        try {
            const data = await productService.getAllProducts(true);
            setProducts(data || []);
            const autoCodes = (data || []).filter(p => p.stock_calculation_type === 'auto').map(p => p.product_code);
            if (autoCodes.length > 0) {
                try {
                    const bulkStock = await inventoryService.calculateBulkProductStock(autoCodes);
                    setBulkCalculatedStock(bulkStock);
                } catch {
                    setBulkCalculatedStock({});
                }
            } else {
                setBulkCalculatedStock({});
            }
        } catch { setError(t('failed_to_load_products')); }
    }, []);

    const [addonUsageMap, setAddonUsageMap] = useState({});
    const loadAddons = useCallback(async () => {
        try {
            const data = await addonService.getAllAddons();
            setAddons(data || []);
        } catch { setError(t('failed_to_load_addons')); }
    }, []);
    const loadAddonUsage = useCallback(async () => {
        try { setAddonUsageMap(await productService.getAddonUsageMap()); } catch {}
    }, []);
    // Only feeds the "Option Groups" tab-count badge — ModifierGroupsTab loads
    // its own copy for the tab body (same split as addons/loadAddons above).
    const loadModifierGroups = useCallback(async () => {
        try {
            const data = await modifierService.getGroups();
            setModifierGroups(data || []);
        } catch { /* tab-count badge only; the tab body reports its own load error */ }
    }, []);

    useEffect(() => {
        setLoading(true);
        Promise.all([loadProducts(), loadAddons(), loadAddonUsage(), loadModifierGroups()]).finally(() => setLoading(false));
    }, [loadProducts, loadAddons, loadAddonUsage, loadModifierGroups]);

    // Deep-link: /portal/products?open=<code>&tab=recipe opens the product modal
    // pre-focused on the requested tab. Used by the Cost Data Health click-through
    // on the Cost Management page. Runs once products are loaded, then strips the
    // params so a refresh doesn't reopen on every navigation.
    const [searchParams, setSearchParams] = useSearchParams();
    useEffect(() => {
        const code = searchParams.get('open');
        if (!code || loading || products.length === 0) return;
        const matched = products.find(p => p.product_code === code);
        if (!matched) return;
        const requestedTab = searchParams.get('tab') === 'recipe' ? 'recipe' : 'details';
        setProductModal({ open: true, product: matched, defaultTab: requestedTab });
        setSearchParams({}, { replace: true });
    }, [loading, products, searchParams, setSearchParams]);

    useEffect(() => {
        if (success) { const timer = setTimeout(() => setSuccess(''), 3000); return () => clearTimeout(timer); }
    }, [success]);

    // ── Derived data ───────────────────────────────────────────────────────────
    const categories = useMemo(() => ['All', ...Array.from(new Set(products.map(p => p.category).filter(Boolean))).sort()], [products]);

    const [bulkCategories, setBulkCategories] = useState([]);
    useEffect(() => { categoryService.getAll().then(setBulkCategories).catch(() => {}); }, []);
    const subcatMap = useMemo(() => {
        const map = {};
        products.forEach(p => {
            if (p.category && p.subcategory) {
                if (!map[p.category]) map[p.category] = new Set();
                map[p.category].add(p.subcategory);
            }
        });
        const result = {};
        Object.keys(map).forEach(k => { result[k] = Array.from(map[k]).sort(); });
        return result;
    }, [products]);

    const filteredProducts = useMemo(() => products.filter(p => {
        if (!includeInactive && p.status?.toLowerCase() !== 'active') return false;
        if (selectedCategory !== 'All' && p.category !== selectedCategory) return false;
        if (stockFilter === 'recipe') {
            const hasRecipe = p.stock_calculation_type === 'auto' || p.cost_source === 'recipe' || p.cost_source === 'recipe_partial';
            if (!hasRecipe) return false;
        } else if (stockFilter === 'no_recipe') {
            const hasRecipe = p.stock_calculation_type === 'auto' || p.cost_source === 'recipe' || p.cost_source === 'recipe_partial';
            if (hasRecipe) return false;
        }
        if (search) {
            const q = search.toLowerCase();
            return p.name?.toLowerCase().includes(q) || p.product_code?.toLowerCase().includes(q) || p.category?.toLowerCase().includes(q);
        }
        return true;
    }).sort((a, b) => {
        const catCmp = (a.category || '').localeCompare(b.category || '');
        return catCmp !== 0 ? catCmp : (a.name || '').localeCompare(b.name || '');
    }), [products, includeInactive, selectedCategory, search, stockFilter]);

    const filteredAddons = useMemo(() => addons.filter(a => {
        if (!search) return true;
        const q = search.toLowerCase();
        const cats = Array.isArray(a.category) ? a.category : (typeof a.category === 'string' && a.category.startsWith('[') ? (() => { try { return JSON.parse(a.category); } catch { return [a.category]; } })() : a.category ? [a.category] : []);
        return a.name?.toLowerCase().includes(q) || cats.some(c => c.toLowerCase().includes(q));
    }), [addons, search]);

    // ── CRUD ───────────────────────────────────────────────────────────────────
    const handleSaveProduct = async (formData) => {
        if (productModal.product && !productModal._isDuplicate) {
            await productService.updateProduct(productModal.product.product_code, formData);
            setSuccess(t('product_updated'));
        } else {
            const created = await productService.createProduct(formData);
            const actualCode = created?.product_code || formData.product_code;
            if (productModal._clonedRecipe?.length > 0 && actualCode) {
                try {
                    await inventoryService.setProductIngredients(
                        actualCode,
                        productModal._clonedRecipe
                    );
                } catch (e) { console.error('Recipe clone failed:', e); }
            }
            setSuccess(t(productModal._isDuplicate ? 'product_duplicated' : 'product_added'));
        }
        await loadProducts();
    };

    const handleDuplicate = async (p) => {
        const newCode = generateProductCode(p.category, products);
        let clonedRecipe = [];
        try {
            const recipe = await inventoryService.getProductIngredients(p.product_code);
            if (recipe?.length > 0) {
                clonedRecipe = recipe.map(r => ({
                    inventory_id: r.inventory_id,
                    quantity_needed: r.quantity_needed,
                    dining_option: r.dining_option || 'both',
                }));
            }
        } catch (e) { /* no recipe */ }
        setProductModal({
            open: true,
            product: {
                ...p,
                name: `Copy of ${p.name}`,
                product_code: newCode,
            },
            _isDuplicate: true,
            _clonedRecipe: clonedRecipe,
        });
    };

    const handleSaveAddon = async (formData) => {
        if (addonModal.addon) {
            await addonService.updateAddon(addonModal.addon.product_code, formData);
            setSuccess(t('addon_updated'));
        } else {
            await addonService.createAddon(formData);
            setSuccess(t('addon_added'));
        }
        await loadAddons();
    };

    const handleDelete = async (reason) => {
        const { item, type, isDeactivate } = deleteModal;
        if (type === 'product') {
            if (isDeactivate) await productService.deactivateProduct(item.product_code, reason);
            else await productService.deleteProduct(item.product_code);
            setSuccess(isDeactivate ? t('product_deactivated') : t('product_deleted'));
            await loadProducts();
        } else {
            await addonService.deleteAddon(item.product_code);
            setSuccess(t('addon_deleted'));
            await loadAddons();
        }
    };

    // ── Bulk selection helpers ─────────────────────────────────────────────────
    const toggleProductSelect = (code) => {
        setSelectedProducts(prev =>
            prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code]
        );
    };
    const toggleAllProducts = (list) => {
        setSelectedProducts(prev =>
            prev.length === list.length ? [] : list.map(p => p.product_code)
        );
    };
    const handleBulkStatus = async (isActive) => {
        try {
            await productService.bulkUpdateStatus(selectedProducts, isActive);
            setSelectedProducts([]);
            await loadProducts();
            setSuccess(t(isActive ? 'products_activated' : 'products_deactivated'));
        } catch (e) {
            setError(t('bulk_action_failed'));
        }
    };

    // Clear selection on tab/filter change
    useEffect(() => { setSelectedProducts([]); }, [tab]);

    // ── Shared styles ──────────────────────────────────────────────────────────
    const thStyle = { padding: '10px 14px', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.8, textAlign: 'left', borderBottom: '1.5px solid #e2e8f0', background: '#f8fafc', whiteSpace: 'nowrap', position: 'sticky', top: 0, zIndex: 2 };
    const tdStyle = { padding: '12px 14px', fontSize: 13, color: '#212529', borderBottom: '1px solid #f1f5f9', verticalAlign: 'middle' };

    return (
        <PortalLayout>
            <div style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", color: '#212529' }}>

                {/* Toasts */}
                {(error || success) && (
                    <div style={{ position: 'fixed', top: 20, right: 20, zIndex: 2000 }}>
                        {error && <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 16px', fontSize: 13, color: '#dc2626', fontWeight: 500 }}>{error}</div>}
                        {success && <div style={{ background: '#eff5ff', border: '1px solid #86efac', borderRadius: 8, padding: '10px 16px', fontSize: 13, color: '#16a34a', fontWeight: 500 }}>{success}</div>}
                    </div>
                )}

                {/* Page header (desktop) */}
                {!isMobile && (<>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 20 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', gap: 2, background: '#f1f5f9', borderRadius: 9, padding: 3 }}>
                            {[['products', t('products')], ['addons', t('add_ons')], ['groups', t('option_groups')]].map(([key, label]) => (
                                <button key={key} onClick={() => { setTab(key); setSearch(''); setSelectedCategory('All'); }}
                                    style={{ padding: '6px 18px', borderRadius: 7, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600, background: tab === key ? '#fff' : 'transparent', color: tab === key ? '#212529' : '#64748b', boxShadow: tab === key ? '0 1px 4px rgba(0,0,0,0.08)' : 'none', transition: 'all 0.15s' }}>
                                    {label}
                                    <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 700, color: tab === key ? '#2563eb' : '#94a3b8', background: tab === key ? '#eff5ff' : 'transparent', borderRadius: 10, padding: '1px 6px' }}>
                                        {key === 'products'
                                            ? products.filter(p => p.status?.toLowerCase() === 'active').length
                                            : key === 'addons' ? addons.length : modifierGroups.length}
                                    </span>
                                </button>
                            ))}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            {tab === 'products' && (
                                <button onClick={() => setShowBulkAdd(true)} style={{
                                    padding: '8px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                                    border: '1px solid #2563eb', backgroundColor: '#fff', color: '#2563eb', cursor: 'pointer'
                                }}>{t('bulk_add')}</button>
                            )}
                            {tab === 'products' && (
                                <button onClick={() => setShowRecipeImport(true)} style={{
                                    padding: '8px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                                    border: '1px solid #2563eb', backgroundColor: '#fff', color: '#2563eb', cursor: 'pointer'
                                }}>{t('import_recipes')}</button>
                            )}
                            {tab !== 'groups' && (
                                <button onClick={() => tab === 'products' ? setProductModal({ open: true, product: null }) : setAddonModal({ open: true, addon: null })}
                                    style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: '#2563eb', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                                    {tab === 'products' ? t('add_product') : t('add_add_on')}
                                </button>
                            )}
                        </div>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        {/* Search */}
                        <div style={{ position: 'relative' }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)' }}><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                            <input value={search} onChange={e => setSearch(e.target.value)} placeholder={t('search_placeholder')}
                                style={{ paddingLeft: 32, paddingRight: 12, paddingTop: 7, paddingBottom: 7, border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13, width: 200, color: '#212529', outline: 'none' }} />
                        </div>

                        {tab === 'products' && (
                            <>
                                {/* Grid/Table toggle */}
                                <div style={{ display: 'flex', border: '1.5px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
                                    {[['table', <svg key="t" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="3" y1="15" x2="21" y2="15"/><line x1="9" y1="9" x2="9" y2="21"/></svg>],
                                     ['grid', <svg key="g" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>]
                                    ].map(([v, icon]) => (
                                        <button key={v} onClick={() => setViewMode(v)} title={v.charAt(0).toUpperCase() + v.slice(1)}
                                            style={{ padding: '6px 10px', border: 'none', cursor: 'pointer', background: viewMode === v ? '#2563eb' : '#fff', color: viewMode === v ? '#fff' : '#64748b' }}>
                                            {icon}
                                        </button>
                                    ))}
                                </div>
                                {/* Stock type filter */}
                                <select value={stockFilter} onChange={e => setStockFilter(e.target.value)} style={{
                                    padding: '6px 10px', borderRadius: 6, border: '1.5px solid #e2e8f0', fontSize: 12,
                                    color: '#64748b', cursor: 'pointer', background: '#fff',
                                }}>
                                    <option value="all">{t('all_products')}</option>
                                    <option value="recipe">{t('has_recipe')}</option>
                                    <option value="no_recipe">{t('no_recipe')}</option>
                                </select>
                                {/* Include inactive */}
                                <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 12, color: '#64748b', fontWeight: 500 }}>
                                    <input type="checkbox" checked={includeInactive} onChange={e => setIncludeInactive(e.target.checked)} style={{ accentColor: '#2563eb', width: 14, height: 14, cursor: 'pointer' }} />
                                    {t('show_inactive')}
                                </label>
                            </>
                        )}

                    </div>
                </div>

                {/* Category filter pills (products tab only, desktop) */}
                {tab === 'products' && categories.length > 1 && (
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 16 }}>
                        {categories.map(cat => (
                            <button key={cat} onClick={() => setSelectedCategory(cat)}
                                style={{ padding: '5px 14px', borderRadius: 20, border: '1.5px solid', borderColor: selectedCategory === cat ? '#2563eb' : '#e2e8f0', background: selectedCategory === cat ? '#2563eb' : '#fff', color: selectedCategory === cat ? '#fff' : '#475569', fontSize: 12, fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s' }}>
                                {cat}
                                {cat !== 'All' && <span style={{ marginLeft: 5, opacity: 0.75 }}>({products.filter(p => p.category === cat && (includeInactive || p.status?.toLowerCase() === 'active')).length})</span>}
                            </button>
                        ))}
                    </div>
                )}
                </>)}

                {/* Page header (mobile) — tabs, search, category chips, filters, actions */}
                {isMobile && (
                    <div style={{ marginBottom: 14 }}>
                        {/* Tabs */}
                        <div style={{ display: 'flex', gap: 2, background: '#f1f5f9', borderRadius: 10, padding: 3, marginBottom: 12 }}>
                            {[['products', t('products')], ['addons', t('add_ons')], ['groups', t('option_groups')]].map(([key, label]) => (
                                <button key={key} onClick={() => { setTab(key); setSearch(''); setSelectedCategory('All'); }}
                                    style={{ flex: 1, padding: '9px 8px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 700, background: tab === key ? '#fff' : 'transparent', color: tab === key ? '#212529' : '#64748b', boxShadow: tab === key ? '0 1px 4px rgba(0,0,0,0.08)' : 'none' }}>
                                    {label}
                                    <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 700, color: tab === key ? '#2563eb' : '#94a3b8', background: tab === key ? '#eff5ff' : 'transparent', borderRadius: 10, padding: '1px 6px' }}>
                                        {key === 'products'
                                            ? products.filter(p => p.status?.toLowerCase() === 'active').length
                                            : key === 'addons' ? addons.length : modifierGroups.length}
                                    </span>
                                </button>
                            ))}
                        </div>
                        {/* Search */}
                        <div style={{ position: 'relative', marginBottom: 10 }}>
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)' }}><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
                            <input value={search} onChange={e => setSearch(e.target.value)} placeholder={t('search_placeholder')}
                                style={{ width: '100%', padding: '11px 12px 11px 36px', border: '1.5px solid #e2e8f0', borderRadius: 10, fontSize: 14, color: '#212529', outline: 'none', boxSizing: 'border-box' }} />
                        </div>
                        {/* Category chips — horizontal scroll */}
                        {tab === 'products' && categories.length > 1 && (
                            <div className="portal-hscroll" style={{ display: 'flex', gap: 6, marginBottom: 12, paddingBottom: 2 }}>
                                {categories.map(cat => (
                                    <button key={cat} onClick={() => setSelectedCategory(cat)}
                                        style={{ flexShrink: 0, padding: '7px 14px', borderRadius: 20, border: '1.5px solid', borderColor: selectedCategory === cat ? '#2563eb' : '#e2e8f0', background: selectedCategory === cat ? '#2563eb' : '#fff', color: selectedCategory === cat ? '#fff' : '#475569', fontSize: 12.5, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                                        {cat}{cat !== 'All' && <span style={{ marginLeft: 5, opacity: 0.75 }}>({products.filter(p => p.category === cat && (includeInactive || p.status?.toLowerCase() === 'active')).length})</span>}
                                    </button>
                                ))}
                            </div>
                        )}
                        {/* Filter row */}
                        {tab === 'products' && (
                            <div style={{ display: 'flex', gap: 10, marginBottom: 12, alignItems: 'center' }}>
                                <select value={stockFilter} onChange={e => setStockFilter(e.target.value)} style={{ flex: 1, minWidth: 0, padding: '9px 10px', borderRadius: 8, border: '1.5px solid #e2e8f0', fontSize: 13, color: '#475569', cursor: 'pointer', background: '#fff' }}>
                                    <option value="all">{t('all_products')}</option>
                                    <option value="recipe">{t('has_recipe')}</option>
                                    <option value="no_recipe">{t('no_recipe')}</option>
                                </select>
                                <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 12.5, color: '#64748b', fontWeight: 600, whiteSpace: 'nowrap' }}>
                                    <input type="checkbox" checked={includeInactive} onChange={e => setIncludeInactive(e.target.checked)} style={{ accentColor: '#2563eb', width: 16, height: 16, cursor: 'pointer' }} />
                                    {t('show_inactive')}
                                </label>
                            </div>
                        )}
                        {/* Actions */}
                        <div style={{ display: 'flex', gap: 8 }}>
                            {tab === 'products' && (
                                <button onClick={() => setShowBulkAdd(true)} style={{ flex: 1, padding: 12, borderRadius: 10, fontSize: 13, fontWeight: 700, border: '1.5px solid #2563eb', background: '#fff', color: '#2563eb', cursor: 'pointer' }}>{t('bulk_add')}</button>
                            )}
                            {tab === 'products' && (
                                <button onClick={() => setShowRecipeImport(true)} style={{ flex: 1, padding: 12, borderRadius: 10, fontSize: 13, fontWeight: 700, border: '1.5px solid #2563eb', background: '#fff', color: '#2563eb', cursor: 'pointer' }}>{t('import_recipes')}</button>
                            )}
                            {tab !== 'groups' && (
                                <button onClick={() => tab === 'products' ? setProductModal({ open: true, product: null }) : setAddonModal({ open: true, addon: null })}
                                    style={{ flex: 1, padding: 12, borderRadius: 10, border: 'none', background: '#2563eb', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                                    {tab === 'products' ? t('add_product') : t('add_add_on')}
                                </button>
                            )}
                        </div>
                    </div>
                )}

                {loading ? (
                    <div style={{ background: '#fff', borderRadius: 12, border: '1.5px solid #e2e8f0', padding: 48, textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>{t('loading')}</div>
                ) : (
                    <>
                        {/* ── Products Tab ─────────────────────────────────────────────────── */}
                        {tab === 'products' && (
                            <>
                                {filteredProducts.length === 0 ? (
                                    <div style={{ background: '#fff', borderRadius: 12, border: '1.5px solid #e2e8f0', padding: 56, textAlign: 'center' }}>
                                        <div style={{ width: 52, height: 52, borderRadius: '50%', background: '#eff5ff', margin: '0 auto 16px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="1.5"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/></svg>
                                        </div>
                                        <div style={{ fontSize: 15, fontWeight: 700, color: '#212529', marginBottom: 6 }}>
                                            {search || selectedCategory !== 'All' ? t('no_products_match_filters') : t('no_products_yet')}
                                        </div>
                                        <div style={{ fontSize: 13, color: '#64748b', marginBottom: 20 }}>
                                            {search || selectedCategory !== 'All' ? t('try_adjusting_filters') : t('add_first_product_desc')}
                                        </div>
                                        {!search && selectedCategory === 'All' && (
                                            <button onClick={() => setProductModal({ open: true, product: null })} style={{ padding: '9px 20px', borderRadius: 8, border: 'none', background: '#2563eb', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>{t('add_first_product')}</button>
                                        )}
                                    </div>
                                ) : isMobile ? (
                                    <div>
                                        {filteredProducts.map(p => {
                                            const active = (p.status || '').toLowerCase() === 'active';
                                            const cost = p.effective_cost_price != null ? p.effective_cost_price : Number(p.cost_price || 0);
                                            const kv = (label, value) => (
                                                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 13 }}>
                                                    <span style={{ color: '#475569' }}>{label}</span>
                                                    <span style={{ fontWeight: 700, color: '#212529' }}>{value}</span>
                                                </div>
                                            );
                                            return (
                                                <MobileCard
                                                    key={p.product_code}
                                                    accent={!active ? 'red' : undefined}
                                                    avatar={{ initials: (p.name || '?').charAt(0).toUpperCase(), tone: active ? 'green' : 'gray' }}
                                                    title={p.name}
                                                    amount={`${currency}${parseFloat(p.selling_price || 0).toFixed(2)}`}
                                                    status={{ label: active ? (t('active') || 'Active') : (t('inactive') || 'Inactive'), tone: active ? 'green' : 'gray' }}
                                                    subtitle={p.subcategory ? `${p.category} · ${p.subcategory}` : p.category}
                                                >
                                                    {kv(t('code') || 'Code', p.product_code)}
                                                    {kv(t('category') || 'Category', p.category)}
                                                    {kv(t('price') || 'Price', `${currency}${parseFloat(p.selling_price || 0).toFixed(2)}`)}
                                                    {kv(t('cost') || 'Cost', cost > 0 ? `${currency}${Number(cost).toFixed(2)}` : '—')}
                                                    <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                                                        <button onClick={() => setProductModal({ open: true, product: p })} style={{ flex: 1, padding: '11px 12px', borderRadius: 8, border: '1.5px solid #e2e8f0', background: '#fff', color: '#475569', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>{t('edit') || 'Edit'}</button>
                                                        <button onClick={() => handleDuplicate(p)} style={{ flex: 1, padding: '11px 12px', borderRadius: 8, border: '1.5px solid #e2e8f0', background: '#fff', color: '#475569', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>{t('duplicate') || 'Duplicate'}</button>
                                                        <button onClick={() => setDeleteModal({ open: true, item: p, type: 'product', isDeactivate: active })} style={{ padding: '11px 14px', borderRadius: 8, border: '1.5px solid #fecaca', background: '#fef2f2', color: '#dc2626', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>{active ? (t('deactivate') || 'Deactivate') : (t('delete') || 'Delete')}</button>
                                                    </div>
                                                </MobileCard>
                                            );
                                        })}
                                    </div>
                                ) : viewMode === 'table' ? (
                                    <div className="portal-table-wrap" style={{ background: '#fff', borderRadius: 12, border: '1.5px solid #e2e8f0', overflow: 'auto' }}>
                                        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
                                            <thead>
                                                <tr>
                                                    <th style={{ ...thStyle, width: 40 }}>
                                                        <input type="checkbox" checked={selectedProducts.length === filteredProducts.length && filteredProducts.length > 0} onChange={() => toggleAllProducts(filteredProducts)} />
                                                    </th>
                                                    <th style={thStyle}>{t('product')}</th>
                                                    <th style={thStyle}>{t('code')}</th>
                                                    <th style={thStyle}>{t('category')}</th>
                                                    <th style={thStyle}>{t('subcategory')}</th>
                                                    <th style={{ ...thStyle, textAlign: 'right' }}>{t('price')}</th>
                                                    <th style={{ ...thStyle, textAlign: 'right' }}>{t('cost')}</th>
                                                    <th style={thStyle}>{t('stock')}</th>
                                                    <th style={thStyle}>{t('status')}</th>
                                                    <th style={{ ...thStyle, textAlign: 'right' }}>{t('actions')}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filteredProducts.map((p, i) => (
                                                    <tr key={p.product_code}
                                                        onClick={e => { if (e.target.closest('button, input[type="checkbox"]')) return; setProductModal({ open: true, product: p }); }}
                                                        style={{ cursor: 'pointer' }}
                                                        onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                                                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                                                        <td style={tdStyle}>
                                                            <input type="checkbox" checked={selectedProducts.includes(p.product_code)} onChange={() => toggleProductSelect(p.product_code)} />
                                                        </td>
                                                        <td style={tdStyle}>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                                <ProductAvatar name={p.name} imageUrl={p.image_url} size={32} />
                                                                <span style={{ fontWeight: 600 }}>{p.name}</span>
                                                            </div>
                                                        </td>
                                                        <td style={{ ...tdStyle, color: '#64748b', fontFamily: 'monospace', fontSize: 12 }}>{p.product_code}</td>
                                                        <td style={tdStyle}>{p.category}</td>
                                                        <td style={{ ...tdStyle, color: '#64748b' }}>{p.subcategory || '—'}</td>
                                                        <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}>{currency}{parseFloat(p.selling_price || 0).toFixed(2)}</td>
                                                        <td style={{ ...tdStyle, textAlign: 'right', color: '#64748b' }}>
                                                            <CostWithSource product={p} currency={currency} t={t} />
                                                        </td>
                                                        <td style={tdStyle}><StockBadge product={p} autoStock={bulkCalculatedStock[p.product_code]?.available_stock} t={t} /></td>
                                                        <td style={tdStyle}><StatusBadge status={p.status} t={t} /></td>
                                                        <td style={{ ...tdStyle, textAlign: 'right' }}>
                                                            <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                                                                <button onClick={() => setProductModal({ open: true, product: p })}
                                                                    style={{ padding: '5px 12px', borderRadius: 6, border: '1.5px solid #e2e8f0', background: '#fff', color: '#475569', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                                                                    {t('edit')}
                                                                </button>
                                                                <button onClick={() => handleDuplicate(p)}
                                                                    style={{ padding: '5px 12px', borderRadius: 6, border: '1.5px solid #e2e8f0', background: '#fff', color: '#475569', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                                                                    {t('duplicate')}
                                                                </button>
                                                                {p.status?.toLowerCase() === 'active' ? (
                                                                    <button onClick={() => setDeleteModal({ open: true, item: p, type: 'product', isDeactivate: true })}
                                                                        style={{ padding: '5px 12px', borderRadius: 6, border: '1.5px solid #fecaca', background: '#fef2f2', color: '#dc2626', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                                                                        {t('deactivate')}
                                                                    </button>
                                                                ) : (
                                                                    <button onClick={() => setDeleteModal({ open: true, item: p, type: 'product', isDeactivate: false })}
                                                                        style={{ padding: '5px 12px', borderRadius: 6, border: '1.5px solid #fecaca', background: '#fef2f2', color: '#dc2626', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                                                                        {t('delete')}
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                        <div style={{ padding: '10px 16px', borderTop: '1px solid #f1f5f9', fontSize: 12, color: '#94a3b8', background: '#fafafa' }}>
                                            {filteredProducts.length} {filteredProducts.length !== 1 ? t('products') : t('product')}
                                        </div>
                                    </div>
                                ) : (
                                    // Grid view
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 14 }}>
                                        {filteredProducts.map(p => (
                                            <div key={p.product_code} style={{ background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 12, overflow: 'hidden', transition: 'box-shadow 0.15s', cursor: 'default' }}
                                                onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.08)'}
                                                onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}>
                                                {/* Image / emoji / fallback */}
                                                <div style={{ height: 100, background: `linear-gradient(135deg, ${avatarColor(p.name)}22, ${avatarColor(p.name)}44)`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                    <ProductAvatar name={p.name} imageUrl={p.image_url} size={52} />
                                                </div>
                                                <div style={{ padding: '12px 14px' }}>
                                                    <div style={{ fontSize: 13, fontWeight: 700, color: '#212529', marginBottom: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</div>
                                                    <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 8 }}>{p.category}{p.subcategory ? ` · ${p.subcategory}` : ''}</div>
                                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                                                        <span style={{ fontSize: 14, fontWeight: 700, color: '#2563eb' }}>{currency}{parseFloat(p.selling_price || 0).toFixed(2)}</span>
                                                        <StockBadge product={p} autoStock={bulkCalculatedStock[p.product_code]?.available_stock} t={t} />
                                                    </div>
                                                    <div style={{ display: 'flex', gap: 6 }}>
                                                        <button onClick={() => setProductModal({ open: true, product: p })}
                                                            style={{ flex: 1, padding: '6px 0', borderRadius: 6, border: '1.5px solid #e2e8f0', background: '#fff', color: '#475569', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                                                            {t('edit')}
                                                        </button>
                                                        <button onClick={() => handleDuplicate(p)}
                                                            style={{ padding: '6px 10px', borderRadius: 6, border: '1.5px solid #e2e8f0', background: '#fff', color: '#475569', fontSize: 11, cursor: 'pointer' }}
                                                            title={t('duplicate')}>
                                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>
                                                        </button>
                                                        <button onClick={() => setDeleteModal({ open: true, item: p, type: 'product', isDeactivate: p.status?.toLowerCase() === 'active' })}
                                                            style={{ padding: '6px 10px', borderRadius: 6, border: '1.5px solid #fecaca', background: '#fef2f2', color: '#dc2626', fontSize: 11, cursor: 'pointer' }}>
                                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/></svg>
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </>
                        )}

                        {/* ── Add-ons Tab ──────────────────────────────────────────────────── */}
                        {tab === 'addons' && (
                            filteredAddons.length === 0 ? (
                                <div style={{ background: '#fff', borderRadius: 12, border: '1.5px solid #e2e8f0', padding: 56, textAlign: 'center' }}>
                                    <div style={{ width: 52, height: 52, borderRadius: '50%', background: '#eff5ff', margin: '0 auto 16px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="1.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></svg>
                                    </div>
                                    <div style={{ fontSize: 15, fontWeight: 700, color: '#212529', marginBottom: 6 }}>
                                        {search ? t('no_addons_match_search') : t('no_addons_yet')}
                                    </div>
                                    <div style={{ fontSize: 13, color: '#64748b', marginBottom: 20 }}>
                                        {search ? t('try_different_search') : t('addons_description')}
                                    </div>
                                    {!search && (
                                        <button onClick={() => setAddonModal({ open: true, addon: null })} style={{ padding: '9px 20px', borderRadius: 8, border: 'none', background: '#2563eb', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>{t('add_first_addon')}</button>
                                    )}
                                </div>
                            ) : isMobile ? (
                                <div>
                                    {filteredAddons.map(a => {
                                        const cats = Array.isArray(a.category) ? a.category : (typeof a.category === 'string' && a.category.startsWith('[') ? (() => { try { return JSON.parse(a.category); } catch { return [a.category]; } })() : a.category ? [a.category] : []);
                                        const usedBy = addonUsageMap[a.product_code] || [];
                                        const kv = (label, value) => (
                                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 13 }}>
                                                <span style={{ color: '#475569' }}>{label}</span>
                                                <span style={{ fontWeight: 700, color: '#212529', textAlign: 'right' }}>{value}</span>
                                            </div>
                                        );
                                        return (
                                            <MobileCard
                                                key={a.product_code}
                                                avatar={{ initials: (a.name || '?').charAt(0).toUpperCase(), tone: 'gray' }}
                                                title={a.name}
                                                amount={`${currency}${parseFloat(a.price || 0).toFixed(2)}`}
                                                subtitle={cats.join(', ')}
                                            >
                                                {kv(t('category') || 'Category', cats.join(', ') || '—')}
                                                {kv(t('price') || 'Price', `${currency}${parseFloat(a.price || 0).toFixed(2)}`)}
                                                {kv(t('used_by') || 'Used by', usedBy.length > 0 ? usedBy.map(p => p.name).join(', ') : (t('not_assigned') || 'Not assigned'))}
                                                <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                                                    <button onClick={() => setAddonModal({ open: true, addon: a })} style={{ flex: 1, padding: '11px 12px', borderRadius: 8, border: '1.5px solid #e2e8f0', background: '#fff', color: '#475569', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>{t('edit') || 'Edit'}</button>
                                                    <button onClick={() => setDeleteModal({ open: true, item: a, type: 'addon', isDeactivate: false })} style={{ padding: '11px 14px', borderRadius: 8, border: '1.5px solid #fecaca', background: '#fef2f2', color: '#dc2626', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>{t('delete') || 'Delete'}</button>
                                                </div>
                                            </MobileCard>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div style={{ background: '#fff', borderRadius: 12, border: '1.5px solid #e2e8f0', overflow: 'hidden' }}>
                                    <div className="portal-table-wrap">
                                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                        <thead>
                                            <tr>
                                                <th style={thStyle}>{t('name')}</th>
                                                <th style={thStyle}>{t('category')}</th>
                                                <th style={{ ...thStyle, textAlign: 'right' }}>{t('price')}</th>
                                                <th style={thStyle}>{t('used_by')}</th>
                                                <th style={{ ...thStyle, textAlign: 'right' }}>{t('actions')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredAddons.map(a => (
                                                <tr key={a.product_code}
                                                    onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                                                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                                                    <td style={tdStyle}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                            <div style={{ width: 30, height: 30, borderRadius: 7, background: avatarColor(a.name), display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
                                                                {(a.name || '?').charAt(0).toUpperCase()}
                                                            </div>
                                                            <span style={{ fontWeight: 600 }}>{a.name}</span>
                                                        </div>
                                                    </td>
                                                    <td style={tdStyle}>{(() => { const cats = Array.isArray(a.category) ? a.category : (typeof a.category === 'string' && a.category.startsWith('[') ? (() => { try { return JSON.parse(a.category); } catch { return [a.category]; } })() : a.category ? [a.category] : []); return cats.join(', '); })()}</td>
                                                    <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}>{currency}{parseFloat(a.price || 0).toFixed(2)}</td>
                                                    <td style={{ ...tdStyle, color: '#64748b' }}>
                                                        {(addonUsageMap[a.product_code] || []).length > 0 ? (
                                                            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                                                                {(addonUsageMap[a.product_code] || []).map(p => (
                                                                    <span key={p.code} style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, background: '#eff5ff', color: '#2563eb', fontWeight: 600 }}>{p.name}</span>
                                                                ))}
                                                            </div>
                                                        ) : (
                                                            <span style={{ fontSize: 12, color: '#94a3b8' }}>{t('not_assigned')}</span>
                                                        )}
                                                    </td>
                                                    <td style={{ ...tdStyle, textAlign: 'right' }}>
                                                        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                                                            <button onClick={() => setAddonModal({ open: true, addon: a })}
                                                                style={{ padding: '5px 12px', borderRadius: 6, border: '1.5px solid #e2e8f0', background: '#fff', color: '#475569', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>{t('edit')}</button>
                                                            <button onClick={() => setDeleteModal({ open: true, item: a, type: 'addon', isDeactivate: false })}
                                                                style={{ padding: '5px 12px', borderRadius: 6, border: '1.5px solid #fecaca', background: '#fef2f2', color: '#dc2626', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>{t('delete')}</button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    </div>
                                    <div style={{ padding: '10px 16px', borderTop: '1px solid #f1f5f9', fontSize: 12, color: '#94a3b8', background: '#fafafa' }}>
                                        {filteredAddons.length} {filteredAddons.length !== 1 ? t('add_ons') : t('add_on')}
                                    </div>
                                </div>
                            )
                        )}

                        {/* ── Option Groups Tab ───────────────────────────────────────────── */}
                        {tab === 'groups' && (
                            <ModifierGroupsTab addons={addons} currency={currency} t={t} toast={toast} />
                        )}
                    </>
                )}
            </div>

            {selectedProducts.length > 0 && (
                <div style={{
                    position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
                    backgroundColor: '#ffffff', borderRadius: 12, padding: '12px 24px',
                    display: 'flex', alignItems: 'center', gap: 16,
                    boxShadow: '0 -2px 12px rgba(0,0,0,0.08)', zIndex: 50, fontFamily: "'Plus Jakarta Sans', sans-serif",
                }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#212529' }}>
                        {selectedProducts.length} {t('selected')}
                    </span>
                    <button onClick={() => handleBulkStatus(true)} style={{
                        padding: '8px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                        border: '1px solid #2563eb', backgroundColor: '#ffffff', color: '#2563eb', cursor: 'pointer'
                    }}>{t('activate')}</button>
                    <button onClick={() => handleBulkStatus(false)} style={{
                        padding: '8px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                        border: '1px solid #dc2626', backgroundColor: '#ffffff', color: '#dc2626', cursor: 'pointer'
                    }}>{t('deactivate')}</button>
                    <button onClick={() => setSelectedProducts([])} style={{
                        padding: '8px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                        border: '1px solid #e2e8f0', backgroundColor: '#ffffff', color: '#64748b', cursor: 'pointer'
                    }}>{t('cancel')}</button>
                </div>
            )}

            <ProductModal
                open={productModal.open}
                onClose={() => setProductModal({ open: false, product: null, defaultTab: undefined })}
                onSave={handleSaveProduct}
                product={productModal.product}
                allProducts={products}
                currency={currency}
                t={t}
                translationDetails={translationDetails}
                toast={toast}
                defaultTab={productModal.defaultTab}
                allGroups={modifierGroups}
                addons={addons}
            />
            {toastMsg && (
                <div style={{
                    position: 'fixed', top: 20, right: 20, zIndex: 1100,
                    padding: '10px 16px', borderRadius: 9, fontSize: 13, fontWeight: 600,
                    background: toastMsg.type === 'success' ? '#eff5ff' : '#fef2f2',
                    border: '1.5px solid ' + (toastMsg.type === 'success' ? '#86efac' : '#fca5a5'),
                    color: toastMsg.type === 'success' ? '#16a34a' : '#dc2626',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                }}>
                    {toastMsg.msg}
                </div>
            )}
            <AddonModal open={addonModal.open} onClose={() => setAddonModal({ open: false, addon: null })} onSave={handleSaveAddon} addon={addonModal.addon} currency={currency} t={t} translationDetails={translationDetails} toast={toast} />
            <DeleteModal open={deleteModal.open} onClose={() => setDeleteModal({ open: false, item: null, type: null, isDeactivate: false })} onConfirm={handleDelete} name={deleteModal.item?.name} isDeactivate={deleteModal.isDeactivate} t={t} />

            <RecipeImportModal
                open={showRecipeImport}
                onClose={() => setShowRecipeImport(false)}
                onImported={() => loadProducts()}
                t={t}
            />

            <BulkAddModal
                open={showBulkAdd}
                onClose={() => setShowBulkAdd(false)}
                onImport={(results) => {
                    loadProducts();
                    setSuccess(t('bulk_import_success').replace('{count}', results.success));
                }}
                config={{
                    title: 'bulk_add_products',
                    instructions: 'bulk_add_products_instructions',
                    columns: [
                        { key: 'name', label: 'Name', required: true, type: 'text' },
                        { key: 'product_code', label: 'Product Code', type: 'text', readOnly: true },
                        { key: 'category', label: 'Category', required: true, type: 'dropdown', options: bulkCategories.map(c => c.name), allowCreate: true, onCreateOption: async (name) => { await categoryService.create({ name }); const cats = await categoryService.getAll(); setBulkCategories(cats); return name; } },
                        { key: 'subcategory', label: 'Subcategory', type: 'dropdown', options: (row) => subcatMap[row.category] || [], dependsOn: 'category', allowCreate: true, onCreateOption: async (name, row) => { const cat = bulkCategories.find(c => c.name === row?.category); if (cat) { await categoryService.createSubcategory({ name, category_id: cat.id }); const subs = await categoryService.getSubcategories(cat.id); subcatMap[row.category] = subs.map(s => s.name); } return name; } },
                        { key: 'selling_price', label: 'Selling Price', required: true, type: 'number' },
                        { key: 'cost_price', label: 'Cost Price', type: 'number' },
                        { key: 'description', label: 'Description', type: 'text' },
                    ],
                    emptyRow: () => ({ name: '', product_code: '', category: '', subcategory: '', selling_price: '', cost_price: '', description: '' }),
                    existingNames: products.map(p => p.name),
                    templateFilename: 'products_template.csv',
                    exampleRows: [
                        { name: 'Iced Latte', product_code: '', category: 'Bar', subcategory: 'Cold Drinks', selling_price: '5.00', cost_price: '1.50', description: '' },
                        { name: 'Chicken Wrap', product_code: '', category: 'Kitchen', subcategory: 'Wraps', selling_price: '8.00', cost_price: '3.00', description: '' },
                    ],
                    createItem: async (row) => productService.createProduct({
                        name: row.name,
                        product_code: row.product_code || '',
                        category: row.category,
                        subcategory: row.subcategory || null,
                        selling_price: parseFloat(row.selling_price),
                        cost_price: parseFloat(row.cost_price) || 0,
                        description: row.description || null,
                    }),
                }}
                t={t}
            />
        </PortalLayout>
    );
};

export default PortalProductsPage;
